import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { TextField, Box, Button, Grid, Checkbox, FormControlLabel, Typography, Modal, Stack, TableFooter, TablePagination, InputLabel, FormControl, CircularProgress, FormLabel, FormGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, Select, MenuItem, Tooltip, Chip } from '@mui/material';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useSucursal } from '../contexts/SucursalContext';
import GestionClientes from '../Clientes/GestionClientes'; // Importa tu componente GestionClientes
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import GestorDescuentos from './GestorDescuentos';
import ResultadoModal from '../components/ResultadoModal';
import { debounce, set } from 'lodash'; // Puedes usar lodash para crear la función debounce
import { useMemo, useCallback } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import GestionFactura from './GestionFactura'; // Importa tu componente GestionFactura
import CondicionesUtilidadesModal from './CondicionesUtilidadesModal'; // Ajusta la ruta
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ComentariosEntregaModal from './ComentariosEntregaModal';
import LockIcon from '@mui/icons-material/Lock';
import { Alert } from '@mui/material';


const tipoTiempoOptions = [
    { value: 'hora', label: 'Hora' },
    { value: 'dia', label: 'Día' },
    { value: 'semana', label: 'Semana' },
    { value: 'mes', label: 'Mes' },
];

const swalWithHighZIndex = Swal.mixin({
    zIndex: 9999
});

const normalizeText = (text = '') =>
    text
        .toString()
        .toLowerCase()
        .normalize('NFD') // separa acentos
        .replace(/[\u0300-\u036f]/g, '') // elimina acentos
        .replace(/[^a-z0-9\s/-]/g, ' ') // reemplaza símbolos/puntuación por espacio
        .replace(/\s+/g, ' ') // compacta múltiples espacios
        .trim();

// Filter inteligente con coincidencias secuenciales y palabras, devolviendo referencias originales

const createSmartFilter = (searchFields, maxResults = 100) => {
  return (options, { inputValue }) => {
    if (!inputValue || inputValue.length < 1) {
      return options.slice(0, Math.min(maxResults, 50)); // primeros 50 sin búsqueda
    }

    const normalizedInput = normalizeText(inputValue);
    const words = normalizedInput.split(/\s+/).filter(w => w.length > 0);

    if (words.length === 0) {
      return options.slice(0, Math.min(maxResults, 50));
    }

    // 🔍 1️⃣ Coincidencias exactas de la frase completa
    const exactPhraseMatches = options.filter(option => {
      return searchFields.some(field => {
        const fieldValue = normalizeText(
          typeof field === "function" ? field(option) : option[field]
        );
        return fieldValue.includes(normalizedInput);
      });
    });

    // ✅ Si hay coincidencias exactas de frase, devolvemos solo esas
    if (exactPhraseMatches.length > 0) {
      return exactPhraseMatches.slice(0, maxResults);
    }

    // 🔍 2️⃣ Si no hay coincidencias exactas, usamos puntuación más flexible
    const scoredOptions = options
      .map(option => {
        let score = 0;
        let wordMatches = 0;
        let sequentialMatches = 0;

        searchFields.forEach(field => {
          const fieldValue = normalizeText(
            typeof field === "function" ? field(option) : option[field]
          );
          if (!fieldValue) return;

          // Coincidencias secuenciales ("las acacias" en orden correcto)
          if (words.length > 1) {
            let lastIndex = -1;
            let inSequence = true;
            for (let i = 0; i < words.length; i++) {
              const idx = fieldValue.indexOf(words[i], lastIndex + 1);
              if (idx > lastIndex) {
                lastIndex = idx;
              } else {
                inSequence = false;
                break;
              }
            }
            if (inSequence) {
              sequentialMatches++;
              score += 10000;
              if (fieldValue.startsWith(words[0])) score += 5000;
            }
          }

          // Coincidencias palabra a palabra
          const fieldWords = fieldValue.split(/\s+/);
          let foundWords = 0;

          words.forEach(word => {
            if (word.length < 2) return; // ignorar palabras cortas
            if (fieldValue.startsWith(word)) {
              score += 3000;
              foundWords++;
            } else if (fieldWords.some(w => w.startsWith(word))) {
              score += 1500;
              foundWords++;
            } else if (fieldWords.includes(word)) {
              score += 1000;
              foundWords++;
            } else if (fieldValue.includes(word)) {
              score += 200;
              foundWords++;
            }
          });

          wordMatches += foundWords;
        });

        const allWordsFound =
          wordMatches >= words.length || sequentialMatches > 0;

        // Penalizar si no se encontraron todas las palabras
        if (!allWordsFound) score = 0;

        return { option, score, allWordsFound, sequentialMatches };
      })
      .filter(item => item.score > 0 && item.allWordsFound) // 👈 sólo coincidencias completas
      .sort((a, b) => {
        // Priorizar secuenciales y luego score alto
        if (a.sequentialMatches !== b.sequentialMatches)
          return b.sequentialMatches - a.sequentialMatches;
        if (b.score !== a.score) return b.score - a.score;

        // En empate, preferir texto más corto (más específico)
        const aText = normalizeText(
          typeof searchFields[0] === "function"
            ? searchFields[0](a.option)
            : a.option[searchFields[0]]
        );
        const bText = normalizeText(
          typeof searchFields[0] === "function"
            ? searchFields[0](b.option)
            : b.option[searchFields[0]]
        );
        return aText.length - bText.length;
      })
      .slice(0, maxResults)
      .map(item => item.option);

    return scoredOptions;
  };
};


const STOP_WORDS = new Set([
    'de', 'del', 'la', 'el', 'los', 'las',
    'y', 'o', 'u', 'en', 'para', 'por', 'con'
]);


// Filtro específico para casos donde queremos búsqueda muy restrictiva
const createRestrictiveFilter = (searchFields, maxResults = 15, analisisAgregados = []) => {
    return (options, { inputValue }) => {
         // 🔒 FILTRAR ANÁLISIS YA AGREGADOS desde el inicio
        const idsAgregados = new Set(analisisAgregados.map(a => a.idTipoAnalisis));
        const opcionesDisponibles = options.filter(option => !idsAgregados.has(option.idTipoAnalisis));
        
        if (!inputValue || inputValue.length < 1) {
            return opcionesDisponibles.slice(0, Math.min(maxResults, 10));
        }
        
        const normalizedInput = normalizeText(inputValue);
        const words = normalizedInput.split(/\s+/).filter(word => word.length > 0 && !STOP_WORDS.has(word));
        
        if (words.length === 0) return opcionesDisponibles.slice(0, Math.min(maxResults, 10));
        
        const hasSequenceSearch = normalizedInput.trim().includes(' ');

        // 🔹 NUEVO: Filtrar por coincidencia exacta de secuencia (prioridad absoluta)
        if (hasSequenceSearch) {
            const sequenceMatches = opcionesDisponibles.filter(option =>
                searchFields.some(field => {
                    const fieldValue = normalizeText(
                        typeof field === 'function' ? field(option) : option[field]
                    );
                    return fieldValue?.includes(normalizedInput);
                })
            );

            if (sequenceMatches.length > 0) {
                return sequenceMatches.slice(0, maxResults);
            }
        }

        const scoredOptions = opcionesDisponibles
            .map(option => {
                let score = 0;
                let exactPhraseMatches = 0;
                let wordMatches = 0;
                let allWordsFound = true;
                let sequentialMatches = 0;
                let startsWithInput = false;
                
                // Evaluar cada campo de búsqueda
                searchFields.forEach(field => {
                    const fieldValue = normalizeText(typeof field === 'function' ? field(option) : option[field]);
                    if (!fieldValue) return;
                    
                     // 🎯 BONUS ESPECIAL: Para búsquedas cortas (3 letras o menos), dar prioridad absoluta a coincidencias exactas
                    if (normalizedInput.length <= 3) {
                        // Coincidencia exacta completa
                        if (fieldValue === normalizedInput) {
                            score += 1000000; // Prioridad máxima absoluta
                            exactPhraseMatches++;
                            return;
                        }
                        // Empieza exactamente con la búsqueda
                        if (fieldValue.startsWith(normalizedInput + ' ')) {
                            score += 500000;
                            startsWithInput = true;
                        }
                    }
                    
                    // 1. MÁXIMA PRIORIDAD: Búsqueda de frase exacta completa
                    if (words.length > 1 && fieldValue.includes(normalizedInput)) {
                        exactPhraseMatches++;
                        score += 100000; // Puntuación máxima para frase exacta
                        
                        // Bonus masivo si la frase está al inicio
                        if (fieldValue.startsWith(normalizedInput)) {
                            score += 50000;
                        }
                        return; // Si encontramos la frase exacta, es suficiente
                    }
                    
                    // 2. SEGUNDA PRIORIDAD: Verificar si todas las palabras aparecen en orden secuencial
                    if (words.length > 1) {
                        let lastFoundIndex = -1;
                        let wordsInSequence = 0;
                        let isSequential = true;
                        
                        for (let i = 0; i < words.length; i++) {
                            const wordIndex = fieldValue.indexOf(words[i], lastFoundIndex + 1);
                            if (wordIndex > lastFoundIndex) {
                                lastFoundIndex = wordIndex;
                                wordsInSequence++;
                            } else {
                                isSequential = false;
                                break;
                            }
                        }
                        
                        if (isSequential && wordsInSequence === words.length) {
                            sequentialMatches++;
                            score += 50000; // Alto puntaje para palabras en secuencia
                            
                            // Bonus si la secuencia empieza al inicio del campo
                            if (fieldValue.startsWith(words[0])) {
                                score += 25000;
                            }
                        }
                    }
                    
                    // 3. TERCERA PRIORIDAD: Evaluar palabras individuales
                    const fieldWords = fieldValue.split(/\s+/);

                    // 🔒 Para inputs muy cortos (ej: T4), exigir match exacto de palabra
                if (normalizedInput.length <= 3) {
                    if (!fieldWords.includes(normalizedInput)) {
                        return;
                    }
                }

                    let foundWordsInField = 0;
                    
                    words.forEach((word, index) => {
                        if (word.length < 1) return; // Permitir palabras más cortas en restrictivo
                        
                        let wordFound = false;
                        
                        // Coincidencia exacta al inicio del campo (máxima puntuación individual)
                        if (fieldValue.startsWith(word)) {
                            score += 10000;
                            wordFound = true;
                        }
                        // Coincidencia exacta al inicio de cualquier palabra del campo
                        else if (fieldWords.some(w => w.startsWith(word))) {
                            score += 5000;
                            wordFound = true;
                        }
                        // Coincidencia de palabra completa
                        else if (fieldWords.some(w => w === word)) {
                            score += 2000;
                            wordFound = true;
                        }
                        // Contiene la palabra (menor puntuación)
                        else if (fieldValue.includes(word)) {
                            score += 500;
                            wordFound = true;
                        }
                        
                        if (wordFound) {
                            foundWordsInField++;
                            wordMatches++;
                            
                            // Bonus por posición: primeras palabras valen más
                            if (index === 0) {
                                score += 1000; // Primera palabra vale más
                            } else if (index === 1) {
                                score += 500; // Segunda palabra
                            }
                        }
                    });
                    
                    // Bonus si se encontraron todas las palabras en este campo
                    if (foundWordsInField === words.length) {
                        score += 5000 * words.length;
                    }
                });
                
                // Verificar si se encontraron todas las palabras buscadas
                allWordsFound = wordMatches >= words.length || exactPhraseMatches > 0 || sequentialMatches > 0;
                
                // BONUS GLOBALES
                // Bonus masivo por coincidencia de frase exacta
                if (exactPhraseMatches > 0) {
                    score += 20000 * exactPhraseMatches;
                }
                
                // Bonus por palabras secuenciales
                if (sequentialMatches > 0) {
                    score += 10000 * sequentialMatches;
                }
                
                // Bonus si todas las palabras fueron encontradas
                if (allWordsFound) {
                    score += 2000 * words.length;
                }
                
                // PENALIZACIONES
                // Penalización severa si no se encontraron todas las palabras
                const missingWords = Math.max(0, words.length - Math.floor(wordMatches / searchFields.length));
                if (missingWords > 0) {
                    score = Math.max(0, score - (missingWords * 5000));
                }
                
                // Penalización por longitud excesiva (preferir opciones más específicas)
                const totalLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(option) : option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                if (totalLength > 80) {
                    score = Math.max(0, score - (totalLength - 80) * 2);
                }
                
                return { 
                    option, 
                    score, 
                    exactPhraseMatches, 
                    sequentialMatches, 
                    wordMatches,
                    allWordsFound,
                    startsWithInput 
                };
            })
            .filter(item => {
                //Para busquedas cortas (≤3 letras), ser mas estricto
                if (normalizedInput.length <= 3) {
                    return item.score > 10000;
                }
                return item.score > 0;
            })
            .sort((a, b) => {
                if (normalizedInput.length <= 3 && a.startsWithInput !== b.startsWithInput) {
                    return b.startsWithInput - a.startsWithInput;
                }

                // 1. Priorizar coincidencias de frase exacta
                if (a.exactPhraseMatches !== b.exactPhraseMatches) {
                    return b.exactPhraseMatches - a.exactPhraseMatches;
                }
                
                // 2. Priorizar coincidencias secuenciales
                if (a.sequentialMatches !== b.sequentialMatches) {
                    return b.sequentialMatches - a.sequentialMatches;
                }
                
                // 3. Priorizar cuando todas las palabras fueron encontradas
                if (a.allWordsFound !== b.allWordsFound) {
                    return b.allWordsFound - a.allWordsFound;
                }
                
                // 4. Priorizar por score total
                if (b.score !== a.score) {
                    return b.score - a.score;
                }
                
                // 5. En caso de empate total, preferir textos más cortos (más específicos)
                const aLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(a.option) : a.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                const bLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(b.option) : b.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                return aLength - bLength;
            })
            // .slice(0, maxResults)
            .map(item => item.option);
            
        return scoredOptions;
    };
};


function GestionOrdenTrabajo({ ordenTrabajo, detalles, onClose, modo, cliente }) {
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
    const { cajaAbiertaDatos, actualizarCajaAbierta, cajaAbierta } = useSucursal();
    const { sucursalSeleccionada } = useSucursal();
    const [clientes, setClientes] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(false);
    const [sucursales, setSucursales] = useState([]);
    const [origenes, setOrigenes] = useState([]);
    const [mediosPago, setMediosPago] = useState([]);
    const [Medicos, setMedicos] = useState([]);
    const [sucursalEntrega, setSucursalEntrega] = useState(sucursalSeleccionada.idSucursal);
    const [IsEditing, setIsEditing] = useState(false);
    const [identidad, setIdentidad] = useState('');
    const [edad, setEdad] = useState('');
    const [sexo, setSexo] = useState('');
    const [direccion, setDireccion] = useState('');
    const [fechaCotizacion, setFechaCotizacion] = useState('');
    const [fechaRecepcion, setFechaRecepcion] = useState('');
    const [fechaEntrega, setFechaEntrega] = useState('');
    const [validez, setValidez] = useState(1);
    const [medidaT, setMedidaT] = useState('');
    const [telefono, setTelefono] = useState('');
    const [correoElectronico, setCorreoElectronico] = useState('');
    const { user } = useUser();
    const [perfil, setPerfil] = useState(null);
    


    // Helper: verifica si la promoción de un perfil aplica al cliente actual (edad en días y sexo)
    const esPromocionAplicableAlCliente = (perfilItem) => {
        if (!perfilItem || perfilItem.porcentaje <= 0) return false;
        if (!clienteSeleccionado || !clienteSeleccionado.fechaNacimiento) return false;

        // Calcular edad del cliente en días
        const fechaNacimiento = new Date(clienteSeleccionado.fechaNacimiento);
        const hoy = new Date();
        const edadEnDias = Math.floor((hoy - fechaNacimiento) / (1000 * 60 * 60 * 24));

        // Validar rango de edad (si la promoción define límites)
        if (perfilItem.edadDesde != null && perfilItem.edadDesde > 0 && edadEnDias < perfilItem.edadDesde) return false;
        if (perfilItem.edadHasta != null && perfilItem.edadHasta > 0 && edadEnDias > perfilItem.edadHasta) return false;

        // Validar sexo (null, 0 o 3 = ambos sexos)
        if (perfilItem.idSexo != null && perfilItem.idSexo !== 0 && perfilItem.idSexo !== 3) {
            if (perfilItem.idSexo !== clienteSeleccionado.idSexo) return false;
        }

        return true;
    };

    // Helper function para validar y limpiar comentarios
    const validarComentario = (comentario) => {
        if (!comentario || typeof comentario !== 'object') return false;
        if (!comentario.comentarios || typeof comentario.comentarios !== 'string') return false;
        const texto = comentario.comentarios.trim();
        return texto.length > 0 && texto.length <= 1000; // Asumiendo límite de 1000 caracteres
    };
    const [perfilDescuento, setPerfilDescuento] = useState(null);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState('');
    const [origen, setOrigen] = useState('');
    const [medioPago, setMedioPago] = useState('');
    const [tipoPago, setTipoPago] = useState('');
    const [tiempo, setTiempo] = useState('');

    const [tipoTiempoSeleccionado, setTipoTiempoSeleccionado] = useState('');
    const [observaciones, setObservaciones] = useState('');

    const [whatsapp, setWhatsapp] = useState(false);
    const [app, setApp] = useState(false);
    const [correo, setCorreo] = useState(false);
    const [impresos, setImpresos] = useState(false);

    const [analisis, setAnalisis] = useState([]);
    const [analisisPrecios, setAnalisisPrecios] = useState([]);

    const [tiposPagos, setTiposPagos] = useState([]);
    const [tiposCotizacion, setTiposCotizacion] = useState([]);
    const [tiposPrecio, setTiposPrecio] = useState([]);
    const [tiposPrecioAnalisis, setTiposPrecioAnalisis] = useState([]);
    // Descuentos movidos a useDescuentosJerarquicos.jsx
    // Inicializamos prioridadDescuento como array vacío para evitar problemas de referencia
    const [prioridadDescuento, setPrioridadDescuento] = useState([]);
    const [medidasTiempo, setMedidasTiempo] = useState([]);
    const [polizas, setPolizas] = useState([]);
    const [polizasOrigen, setpolizasOrigen] = useState([]);
    const [perfiles, setPerfiles] = useState([]);
    const [sexos, setSexos] = useState([]);
    const [tipoPrecio, setTipoPrecio] = useState('');
    const [valor, setValor] = useState('');
    const [cantidad, setCantidad] = useState(1);
    const [descuento, setDescuento] = useState(''); // Re-adding missing descuento state variable
    // Variables de descuento movidas a useDescuentosJerarquicos.jsx
    const [totalPagar, setTotalPagar] = useState('');
    const [valorVenta, setValorVenta] = useState('');
    const [cantidadTotal, setCantidadTotal] = useState('');
    const [descuentoTotal, setDescuentoTotal] = useState(0);
    const [descuentoTotalP, setDescuentoTotalP] = useState(0);

    const [elegible, setElegible] = useState(false);
    const [comentarios, setComentarios] = useState('');

    const [analisisAgregados, setAnalisisAgregados] = useState([]);

    const [analisisSeleccionado, setAnalisisSeleccionado] = useState(null);

    const analisisSeleccionadoRef = useRef(null);

    const [descuentosAplicados, setDescuentosAplicados] = useState([]); // Added missing state variable

    const [searchQuery, setSearchQuery] = useState(''); // Query actual para la búsqueda
    const [inputValue, setInputValue] = useState(''); // Texto que se muestra en el input
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalClientes, setTotalClientes] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [tablePage, setTablePage] = useState(0); // Estado para la página actual de la tabla
    const [rowsPerPage, setRowsPerPage] = useState(50); // Estado para el número de filas por página
    const [openModalCliente, setOpenModalCliente] = useState(false); // Controla la apertura del modal

    const [isAseguradora, setIsAseguradora] = useState(false);
    // Estas variables se manejan ahora por GestorDescuentos y useDescuentosJerarquicos
    const [descuentoGlobal, setDescuentoGlobal] = useState(0);
    const [descuentoGlobalRemitido, setDescuentoGlobalRemitido] = useState(0);

    // Variables para la sección Datos del Paciente Asegurado
    const [poliza, setPoliza] = useState('');
    const [idPoliza, setIdPoliza] = useState('');
    const [idDetallePoliza, setIdDetallePoliza] = useState('');
    const [empresa, setEmpresa] = useState('');
    const [nTransaccion, setNTransaccion] = useState('');
    const [minimo, setMinimo] = useState('');
    const [maximo, setMaximo] = useState('');
    const [nVerificacion, setNVerificacion] = useState('');
    const [copago, setCopago] = useState('');
    const [autorizado, setAutorizado] = useState(false);
    const [deducible, setDeducible] = useState('');
    const [descuentoAsegurado, setDescuentoAsegurado] = useState('');
    const [valorElegible, setValorElegible] = useState('');
    const [noElegibles, setNoElegibles] = useState('');
    const [aseguradora, setAseguradora] = useState('');
    const [valorCopago, setValorCopago] = useState('');
    const [totalAsegurado, setTotalAsegurado] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [apiResult, setApiResult] = useState({ message: '', status: '' });

    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [showGestionFactura, setShowGestionFactura] = useState(false);
    const [datosFactura, setDatosFactura] = useState(null); // Datos a pasar a GestionFactura

    // Estados para el modal
    const [openModalCondiciones, setOpenModalCondiciones] = useState(false);
    const [selectedAnalisis, setSelectedAnalisis] = useState(null);

    const [openComentariosEntregaModal, setOpenComentariosEntregaModal] = useState(false);
    const [comentariosEntrega, setComentariosEntrega] = useState([]);
    const [analisisOriginal, setAnalisisOriginal] = useState([]);

    const [searchAnalisisQuery, setSearchAnalisisQuery] = useState('');
    // CORRECCIÓN: Estado para controlar el input del Autocomplete de análisis
    // Esto soluciona el problema donde el combo no se limpiaba después de presionar Enter
    const [inputAnalisisValue, setInputAnalisisValue] = useState(''); // Nuevo estado para el input del Autocomplete de análisis

    const [isSaving, setIsSaving] = useState(false);
    const [isModificable, setIsModificable] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [openModalDescuento, setOpenModalDescuento] = useState(false);
    const [puedeCambiarDescuento, setPuedeCambiarDescuento] = useState(false);
    const [ultimoValorAutorizado, setUltimoValorAutorizado] = useState(null);
    
    // Estados para el modal de edición de comentarios
    const [openModalEditarComentarios, setOpenModalEditarComentarios] = useState(false);
    const [analisisParaEditar, setAnalisisParaEditar] = useState(null);
    const [comentarioEditando, setComentarioEditando] = useState('');
    const [tipoPrecioEditando, setTipoPrecioEditando] = useState('');
    const [elegibleEditando, setElegibleEditando] = useState(false);
    const [precioEditando, setPrecioEditando] = useState(0);
    const [descuentoEditando, setDescuentoEditando] = useState(0);
    const [tiposPrecioAnalisisEdicion, setTiposPrecioAnalisisEdicion] = useState([]);
    const [descuentoPorcentajeEditando, setDescuentoPorcentajeEditando] = useState(0);
    
    const refAutocompleteAnalisis = useRef();
    const limpiezaEnProcesoRef = useRef(false); // Para controlar cuando estamos limpiando
    const [mostrarPreviewOrden, setMostrarPreviewOrden] = useState(false);
    const [ordenPreview, setOrdenPreview] = useState(null); // objeto con idOrdenMedicaMes e idOrdenTrabajo
    const [continuarDespuesDeModal, setContinuarDespuesDeModal] = useState(false); const [isAdding, setIsAdding] = useState(false);
    const enterLockRef = useRef(false);
    const analisisEnProcesoRef = useRef(new Set());

    const [openReimprimirPreview, setOpenReimprimirPreview] = useState(false);
    const [reimprimirPdfUrl, setReimprimirPdfUrl] = useState(null);
    const [reimprimirLoading, setReimprimirLoading] = useState(false);
    const [reimprimirError, setReimprimirError] = useState(null);
    const [previewOrdenId, setPreviewOrdenId] = useState(null); // Nuevo estado para almacenar el ID de la orden previsualizada
    const [previewOrdenMedicaMes, setPreviewOrdenMedicaMes] = useState(null); // Para almacenar el idOrdenMedicaMes

    // Estados para el modal de etiquetas
    const [openLabelModal, setOpenLabelModal] = useState(false);
    const [availableSamples, setAvailableSamples] = useState([]);
    const [selectedSamples, setSelectedSamples] = useState([]);

    const [lastInput, setLastInput] = useState("");
    const [debouncedInput, setDebouncedInput] = useState("");
    const [lastTyped, setLastTyped] = useState("");

    const descuentoManualRef = useRef(false);

    // const [inputMedico, setInputMedico] = useState('');

    const { tienePermiso } = useAccesos();
    const limit = 10; // Added missing constant for pagination limit

    // 
    useEffect(() => {
        setPuedeCambiarDescuento(tienePermiso('Cambio descuento'));
    }, [tienePermiso]);

    useEffect(() => {
    if (detalles && detalles.length > 0) {
        setAnalisisAgregados(detalles);
        calcularTotales(detalles);
    }
    }, [detalles]);

    useEffect(() => {
    if (ordenTrabajo && ordenTrabajo.idOrdenMedica) {
      obtenerDetallesOrdenTrabajo(ordenTrabajo.idOrdenMedica);
    }
  }, [ordenTrabajo]);
    
    const handleVerCondiciones = (analisis) => {
        console.log(analisis);
        setSelectedAnalisis(analisis);
        setOpenModalCondiciones(true);
    };


    const analisisFiltrados = analisisAgregados.filter(a => {
        if (!searchAnalisisQuery) return true;
        
        const normalizedQuery = normalizeText(searchAnalisisQuery);
        const normalizedAnalisis = normalizeText(a.analisis);
        
        return normalizedAnalisis.includes(normalizedQuery);
    });
    

    useEffect(() => {
        const t = setTimeout(() => setDebouncedInput(lastTyped), 150); // 150 ms debounce
        return () => clearTimeout(t);
    }, [lastTyped]);


    useEffect(() => {
        // ⛔ Si la orden YA trae médico, NO sobrescribirlo
        if (ordenTrabajo?.idMedico) return;

        if (Medicos.length > 0) {
            const medicoPorDefecto = Medicos.find(m => m.idMedico === 2563);
            if (medicoPorDefecto) {
                setMedicoSeleccionado(medicoPorDefecto);
            }
        }
    }, [Medicos]);

    useEffect(() => {
         // ⛔ Si la orden YA trae origen, NO sobrescribirlo
        if (ordenTrabajo?.idOrigen) return;

        if (origenes.length > 0) {
            const origenPorDefecto = origenes.find(m => m.idOrigen === 0);
            if (origenPorDefecto) {
                setOrigen(origenPorDefecto);
            }
        }
    }, [origenes]);

    useEffect(() => {
        if (mediosPago.length > 0) {
            console.log('Buscando medio de pago por defecto (EFECTIVO - id=0)...');
            const medioPagoPorDefecto = mediosPago.find(m => m.id === 0);
            console.log('Medio de pago encontrado:', medioPagoPorDefecto);
            if (medioPagoPorDefecto) {
                setMedioPago(medioPagoPorDefecto);
                console.log('Medio de pago por defecto establecido:', medioPagoPorDefecto);
            }
        }
    }, [mediosPago]);



    useEffect(() => {
        // Si ambos valores están definidos, calcular la fechaEntrega
        if (tiempo && tipoTiempoSeleccionado) {
            const baseDate = new Date(); // Fecha base: ahora (puedes usar otra, por ejemplo, fechaCotizacion)
            const num = parseInt(tiempo, 10);
            if (!isNaN(num)) {
                const newDate = new Date(baseDate); // Clonamos la fecha base
                switch (tipoTiempoSeleccionado) {
                    case 'hora':
                        newDate.setHours(newDate.getHours() + num);
                        break;
                    case 'dia':
                        newDate.setDate(newDate.getDate() + num);
                        break;
                    case 'semana':
                        newDate.setDate(newDate.getDate() + num * 7);
                        break;
                    case 'mes':
                        newDate.setMonth(newDate.getMonth() + num);
                        break;
                    default:
                        break;
                }
                // Formateamos la fecha para un control "datetime-local" (YYYY-MM-DDTHH:MM)
                const pad = (n) => n.toString().padStart(2, '0');
                const yyyy = newDate.getFullYear();
                const mm = pad(newDate.getMonth() + 1);
                const dd = pad(newDate.getDate());
                const hh = pad(newDate.getHours());
                const min = pad(newDate.getMinutes());
                const formatted = `${yyyy}-${mm}-${dd}T${hh}:${min}`;
                setFechaEntrega(formatted);
            }
        }
    }, [tiempo, tipoTiempoSeleccionado]);

    useEffect(() => {
    if (!ordenTrabajo) return;

    const medicoOrden = Medicos.find(m => m.idMedico === ordenTrabajo.idMedico);
    if (medicoOrden) setMedicoSeleccionado(medicoOrden);

    const origenOrden = origenes.find(o => o.idOrigen === ordenTrabajo.idOrigen);
    if (origenOrden) setOrigen(origenOrden);

    }, [ordenTrabajo, Medicos, origenes]);

    // Crear una función debounced para 
    const debouncedFetchClientes = useMemo(
        () =>
            debounce((searchText) => {
                fetchClientes(searchText);
            }, 500),
        []
    );


    // Este efecto ya no es necesario, ahora GestorDescuentos maneja esta lógica



    // Función auxiliar para buscar análisis por texto de forma más exacta
    const buscarAnalisisPorTexto = (textoAnalisis) => {
        if (!textoAnalisis || textoAnalisis.trim() === '') return null;
        
        const textoBusqueda = textoAnalisis.toLowerCase().trim();
        
        // 🔒 Filtrar análisis que ya están agregados
        const idsAgregados = new Set(analisisAgregados.map(a => a.idTipoAnalisis));
        const analisisDisponibles = analisis.filter(a => !idsAgregados.has(a.idTipoAnalisis));
        
        let encontrado = null;
        
        // 1. Buscar por coincidencia exacta (case insensitive) - MÁXIMA PRIORIDAD
        encontrado = analisisDisponibles.find(a => 
            a.descripcionTipoAnalisis.toLowerCase() === textoBusqueda
        );
        if (encontrado) return encontrado;
        
        // 2. Para búsquedas cortas (≤3 letras), buscar coincidencias que empiecen EXACTAMENTE con el texto + espacio
        // Esto evita que "LDL" seleccione "Colesterol total" por contener "ldl" en alguna parte
        if (textoBusqueda.length <= 3) {
            encontrado = analisisDisponibles.find(a => 
                a.descripcionTipoAnalisis.toLowerCase().startsWith(textoBusqueda + ' ')
            );
            if (encontrado) return encontrado;
        }
        
        // 3. Buscar por comienzo de descripción
        encontrado = analisisDisponibles.find(a => 
            a.descripcionTipoAnalisis.toLowerCase().startsWith(textoBusqueda)
        );
        if (encontrado) return encontrado;
        
        // 4. Buscar si el texto es una palabra completa dentro de la descripción
        encontrado = analisisDisponibles.find(a => {
            const words = a.descripcionTipoAnalisis.toLowerCase().split(/\s+/);
            return words.includes(textoBusqueda);
        });
        
        return encontrado; // Puede ser null si no se encontró
    };

    const handleSeleccionAnalisis = async (analisis) => {
        console.log('🔍 [DEBUG] handleSeleccionAnalisis iniciado con:', analisis);

        descuentoManualRef.current = false;
        
        if (!analisis || !analisis.idTipoAnalisis) {
            console.warn('⚠️ [DEBUG] Análisis inválido recibido:', analisis);

            // Limpiar si no hay selección
            setTipoPrecio('');
            setValor('');
            setDescuento('');
            setAnalisisSeleccionado(null);
            setTiposPrecioAnalisis([]); // Limpiar tipos de precio disponibles
            console.log('🧹 [DEBUG] Estados limpiados');
           
            return;
        }

        setAnalisisSeleccionado(analisis);
        const { idTipoAnalisis } = analisis;
        const idSucursal = sucursalSeleccionada?.idSucursal;

        console.log('📋 [DEBUG] idTipoAnalisis:', idTipoAnalisis, 'idSucursal:', idSucursal);

        try {
            // Llamar a la nueva API que devuelve todos los tipos de precio para este análisis y sucursal
            const url = `${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisispreciobySucursal/${idTipoAnalisis}/${idSucursal}`;
            console.log('🌐 [DEBUG] Llamando API:', url);
            
            const response = await fetch(url);

            if (!response.ok) {
                console.error('❌ [DEBUG] Error en response:', response.status, response.statusText);
                throw new Error('Error en la API');
            }

            const apiResult = await response.json();
            console.log('📥 [DEBUG] Respuesta completa de la API:', apiResult);
            
            const preciosData = apiResult.$values || apiResult;
            console.log('💰 [DEBUG] Datos de precios extraídos:', preciosData);

            if (!preciosData || preciosData.length === 0) {
                console.log('⚠️ [DEBUG] No hay datos de precios');
                await Swal.fire({
                    icon: 'warning',
                    title: 'Precio no encontrado',
                    text: 'No se encontró precio para el análisis seleccionado en la sucursal actual.',
                });
                setTipoPrecio('');
                setValor('');
                setDescuento('');
                setTiposPrecioAnalisis([]);
                return;
            }

            // Actualizar los tipos de precio disponibles para este análisis
            const tiposPrecioDisponibles = preciosData.map(precio => ({
                idTipoPrecio: precio.idTipoPrecio,
                descripcionTipoPrecio: precio.descripcionTipoPrecio,
                precio: precio.precio
            })).sort((a, b) => {
                if (a.descripcionTipoPrecio === 'CONSULTA EXTERNA') return -1;
                if (b.descripcionTipoPrecio === 'CONSULTA EXTERNA') return 1;
                return 0;
            });
            
            console.log('🏷️ [DEBUG] Tipos de precio disponibles:', tiposPrecioDisponibles);
            setTiposPrecioAnalisis(tiposPrecioDisponibles);

            // Seleccionar el primer tipo de precio disponible
            const primerTipoPrecio = tiposPrecioDisponibles[0];
            console.log('🥇 [DEBUG] Primer tipo de precio:', primerTipoPrecio);

            // 🔴 VALIDACIÓN: No permitir análisis con precio cero
            if (!primerTipoPrecio.precio || parseFloat(primerTipoPrecio.precio) <= 0) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Precio inválido',
                    text: 'Este análisis tiene precio cero o no tiene precio asignado. No se puede agregar.',
                });
                setAnalisisSeleccionado(null);
                setInputAnalisisValue('');
                setTipoPrecio('');
                setValor('');
                setDescuento('');
                setTiposPrecioAnalisis([]);
                return;
            }
            
            setTipoPrecio(primerTipoPrecio.idTipoPrecio);
            setValor(primerTipoPrecio.precio);
            
            console.log('✅ [DEBUG] Estados actualizados - tipoPrecio:', primerTipoPrecio.idTipoPrecio, 'valor:', primerTipoPrecio.precio);

              // No recalcular descuento si es manual
                if (descuentoManualRef.current) {
                    console.log("Descuento manual activo, no se recalcula");
                    return;
                }

            // Calcular descuento
            const descuentoGlobalAplicable = analisis.remitido ? descuentoGlobalRemitido : descuentoGlobal;

            if (descuentoGlobalAplicable === 0) {
                try {
                    const resultadoDescuento = await obtenerDescuentoPorAnalisis(idTipoAnalisis);
                    const porcentajeDescuento = resultadoDescuento?.porcentaje || 0;
                    setDescuento(porcentajeDescuento);
                } catch (error) {
                    console.error('Error al obtener el descuento:', error);
                    setDescuento(0);
                }
            } else {
                setDescuento(descuentoGlobalAplicable);
            }

        } catch (error) {
            console.error('❌ [DEBUG] Error obteniendo precios por API:', error);
            setTipoPrecio('');
            setValor('');
            setDescuento('');
            setTiposPrecioAnalisis([]);
            await Swal.fire({
                icon: 'warning',
                title: 'Error',
                text: 'Error al obtener los precios del análisis.',
            });
        }
    };

    // Nueva función para manejar el cambio de tipo de precio
    const handleTipoPrecioChange = async (e) => {
        const nuevoTipoPrecio = e.target.value;
        console.log('💰 [DEBUG] handleTipoPrecioChange - nuevoTipoPrecio:', nuevoTipoPrecio);
        setTipoPrecio(nuevoTipoPrecio);

        if (!analisisSeleccionado || !nuevoTipoPrecio) {
            console.log('⚠️ [DEBUG] No hay análisis o tipo de precio, limpiando valor');
            setValor('');
            return;
        }

        // Buscar el precio en los tipos de precio disponibles para este análisis
        const precioSeleccionado = tiposPrecioAnalisis.find(p => p.idTipoPrecio === parseInt(nuevoTipoPrecio));
        console.log('🔍 [DEBUG] Precio seleccionado encontrado:', precioSeleccionado);
        
        if (precioSeleccionado) {
            // 🔴 VALIDACIÓN: Advertir si el tipo de precio seleccionado tiene precio cero
            if (!precioSeleccionado.precio || parseFloat(precioSeleccionado.precio) <= 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Precio cero',
                    text: 'Este tipo de precio tiene valor cero. No podrá agregar el análisis con este precio.',
                });
            }
            setValor(precioSeleccionado.precio);
            console.log('✅ [DEBUG] Valor actualizado a:', precioSeleccionado.precio);
        } else {
            console.log('❌ [DEBUG] No se encontró precio para el tipo seleccionado');
            setValor('');
        }
    };


    // CORRECCIÓN: Esta función maneja el agregado de análisis cuando se presiona Enter
    // Se agregaron logs para debugging y se mejoró la limpieza del Autocomplete
    const handleAgregarAnalisisConParametro = async (analisis) => {
        console.log('🔍 [DEBUG] handleAgregarAnalisisConParametro iniciado con:', analisis?.descripcionTipoAnalisis || 'null');
        
        // Si se proporciona un string en lugar de un objeto análisis, buscar el análisis por su texto
        if (typeof analisis === 'string') {
            const textoAnalisis = analisis.trim();
            console.log('🔎 [DEBUG] Buscando análisis por texto:', textoAnalisis);
            analisis = buscarAnalisisPorTexto(textoAnalisis);
        }
        
        if (!analisis) {
            console.log('❌ [DEBUG] No hay análisis seleccionado');
            Swal.fire({ text: "Por favor, selecciona un análisis válido.", icon: "warning" });
            return;
        }

        if (!clienteSeleccionado) {
            console.log('❌ [DEBUG] No hay cliente seleccionado');
            Swal.fire({ text: "Por favor, selecciona un cliente antes de agregar un análisis.", icon: "warning" });
            return;
        }

        const yaAgregado = analisisAgregados.some(a => a.idTipoAnalisis === analisis.idTipoAnalisis);
        const enProceso = analisisEnProcesoRef.current.has(analisis.idTipoAnalisis);

        if (yaAgregado || enProceso) {
            console.log('⚠️ [DEBUG] Análisis ya agregado o en proceso:', analisis.descripcionTipoAnalisis);
            Swal.fire({ text: "El análisis ya está en proceso o agregado.", icon: "warning" });
            return;
        }

        console.log('✅ [DEBUG] Iniciando proceso de agregar análisis:', analisis.descripcionTipoAnalisis);
        analisisEnProcesoRef.current.add(analisis.idTipoAnalisis);

        try {
            const idTipoAnalisis = analisis.idTipoAnalisis;
            const idSucursal = sucursalSeleccionada?.idSucursal;

            let precioData = analisisPrecios.find(
                (item) => item.idTipoAnalisis === idTipoAnalisis && item.idSucursal === idSucursal
            );

            if (!precioData) {
                const res = await fetch(
                    `${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisispreciobySucursal/${idTipoAnalisis}/${idSucursal}`
                );
                if (!res.ok) throw new Error('Error en la API');
                precioData = await res.json();
                if (!precioData?.precio) throw new Error('Sin datos de precio');
            }

            const valorNumerico = parseFloat(precioData.precio) || 0;

            // 🔴 VALIDACIÓN CRÍTICA: No permitir análisis con precio cero
            if (valorNumerico <= 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Precio inválido',
                    text: `El análisis "${analisis.descripcionTipoAnalisis}" tiene precio cero o no tiene precio asignado. No se puede agregar.`,
                });
                return;
            }

            const tipoPrecioId = precioData.idTipoPrecio;
            const cantidadNumerica = parseInt(cantidad) || 1;

            let porcentajeDescuento = 0;
            let idPromocionAplicada = null;

            if (analisis.remitido && descuentoGlobalRemitido > 0) {
                porcentajeDescuento = descuentoGlobalRemitido;
            } else if (descuentoGlobal > 0) {
                porcentajeDescuento = descuentoGlobal;
            } else {
                const resultadoDescuento = await obtenerDescuentoPorAnalisis(idTipoAnalisis);
                porcentajeDescuento = parseFloat(resultadoDescuento?.porcentaje) || 0;
                idPromocionAplicada = resultadoDescuento?.idPromocion || null;
            }

            const descuentoMonto = (valorNumerico * porcentajeDescuento) / 100;
            const total = (valorNumerico - descuentoMonto) * cantidadNumerica;

            const nuevoAnalisis = {
                idModulo: analisis.idModulo,
                idPerfil: 0,
                perfil: '',
                idTipoAnalisis: analisis.idTipoAnalisis,
                analisis: analisis.descripcionTipoAnalisis,
                cantidad: cantidadNumerica,
                idTipoPrecio: tipoPrecioId,
                precio: valorNumerico,
                descuento: descuentoMonto.toFixed(2),

                porcentajeDescuento: porcentajeDescuento,

                descuentoManual: descuentoManualRef.current, // 🔐 CLAVE

                total: total.toFixed(2),
                elegible: elegible,
                comentarios: comentarios,
                idPromocion: idPromocionAplicada,
                remitido: analisis.remitido
            };

            setAnalisisAgregados((prev) => {
                const updated = [...prev, nuevoAnalisis];
                calcularTotales(updated);
                return updated;
            });

            if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
                recalcularElegiblesNoElegibles([...analisisAgregados, nuevoAnalisis]);
            }

            console.log('🧹 [DEBUG] Iniciando limpieza de campos después de agregar análisis');
            
            // Marcar que estamos en proceso de limpieza
            limpiezaEnProcesoRef.current = true;
            console.log('🔒 [DEBUG] limpiezaEnProcesoRef activado');
            
            // Limpieza inmediata
            setAnalisisSeleccionado(null);
            console.log('🧹 [DEBUG] analisisSeleccionado limpiado a null');
            
            // CORRECCIÓN: Limpiar también el input del Autocomplete
            setInputAnalisisValue('');
            console.log('🧹 [DEBUG] inputAnalisisValue limpiado');
            
            // Limpieza adicional con setTimeout para evitar que el Autocomplete re-seleccione
            setTimeout(() => {
                setAnalisisSeleccionado(null);
                setInputAnalisisValue('');
                if (refAutocompleteAnalisis.current) {
                    refAutocompleteAnalisis.current.value = '';
                }
                limpiezaEnProcesoRef.current = false;
                console.log('🧹 [DEBUG] Limpieza adicional con timeout ejecutada y limpiezaEnProcesoRef liberado');
            }, 200);
            
            if (refAutocompleteAnalisis.current) {
                refAutocompleteAnalisis.current.value = '';
                console.log('🧹 [DEBUG] Input del Autocomplete limpiado via ref');
            }
            
            setValor(0);
            setCantidad(1);
            setDescuento(0);
            setComentarios('');
            console.log('🧹 [DEBUG] Todos los campos limpiados exitosamente');
            setTimeout(() => recalcularDescuentosCondiciones(), 0);

        } catch (err) {
            console.error("❌ [DEBUG] Error al agregar análisis:", err);
        } finally {
            analisisEnProcesoRef.current.delete(analisis.idTipoAnalisis);
            console.log('🔓 [DEBUG] Análisis removido del procesamiento:', analisis.idTipoAnalisis);
        }
    };


    const handlePerfilKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAgregarAnalisisPerfil();
        }
    };
    // Manejar el cambio en el input del Autocomplete
    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        debouncedFetchClientes(newInputValue);
    };


    useEffect(() => {
        console.log("ordenTrabajo en useEffect:", ordenTrabajo);
        if (ordenTrabajo) {
            fetchComentarios(ordenTrabajo.idOrdenMedica);
            // ... otras acciones
        }
    }, [ordenTrabajo]);
    const fetchComentarios = async (idOrden) => {
        try {
            const response = await fetch(`${server}/MgOrdenesMedicasComentariosEntregas/porOrden/${idOrden}`);
            const data = await response.json();
            
            // Asegurar que los datos tengan el formato correcto para el modal
            const comentariosFormateados = data.$values ? data.$values.map(c => ({
                id: c.id,
                comentarios: c.comentarios
            })) : [];
            
            setComentariosEntrega(comentariosFormateados);
            console.log('Comentarios cargados:', comentariosFormateados);
        } catch (error) {
            console.error('Error al cargar los comentarios de entrega:', error);
            setComentariosEntrega([]);
        }
    };

    // ✅ Función para guardar comentarios desde el modal
    const handleGuardarComentariosEntrega = async (comentariosValidos, comentariosEliminados = []) => {
        if (!ordenTrabajo?.idOrdenMedica) {
            console.warn('No hay orden seleccionada para guardar comentarios');
            return;
        }

        try {
            console.log('💾 Guardando comentarios desde GestionOrdenTrabajo');
            console.log('Comentarios válidos:', comentariosValidos);
            console.log('Comentarios a eliminar:', comentariosEliminados);

            // 1. Eliminar comentarios marcados
            const username = user ? `${user.codigoUsuario}/` : 'ADMIN/';
            for (const idComentario of comentariosEliminados) {
                try {
                    const deleteResponse = await fetch(
                        `${server}/MgOrdenesMedicasComentariosEntregas/${idComentario}/${username}`,
                        { method: 'DELETE' }
                    );
                    if (!deleteResponse.ok) {
                        console.error('Error al eliminar comentario:', idComentario, deleteResponse.status);
                    } else {
                        console.log('✅ Comentario eliminado exitosamente:', idComentario);
                    }
                } catch (error) {
                    console.error('Error al eliminar comentario:', error);
                }
            }

            // 2. Guardar solo comentarios nuevos (isNew === true)
            const comentariosNuevos = comentariosValidos.filter(c => c.isNew);
            console.log('📝 Comentarios nuevos a guardar:', comentariosNuevos);

            for (const comentario of comentariosNuevos) {
                const payloadComentario = {
                    idOrdenMedica: ordenTrabajo.idOrdenMedica,
                    comentarios: comentario.comentarios,
                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
                };

                const responseComentario = await fetch(`${server}/MgOrdenesMedicasComentariosEntregas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payloadComentario)
                });

                if (!responseComentario.ok) {
                    console.error('Error al guardar comentario:', responseComentario.statusText);
                }
            }

            // 3. Recargar comentarios actualizados desde la API
            await fetchComentarios(ordenTrabajo.idOrdenMedica);

            // Opcional: mostrar notificación de éxito
            if (comentariosNuevos.length > 0 || comentariosEliminados.length > 0) {
                Swal.fire({
                    icon: 'success',
                    title: 'Comentarios actualizados',
                    text: `${comentariosNuevos.length} guardado(s), ${comentariosEliminados.length} eliminado(s)`,
                    showConfirmButton: false,
                    timer: 1500
                });
            }

            setOpenComentariosEntregaModal(false);
        } catch (error) {
            console.error('Error al guardar comentarios:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron guardar los comentarios'
            });
        }
    };


    // Las funciones de descuentos han sido movidas a useDescuentosJerarquicos.jsx


    // La función calcularDescuento ha sido movida a useDescuentosJerarquicos.jsx




    // Las funciones esTerceraEdad y esCuartaEdad han sido movidas a useDescuentosJerarquicos.jsx

    const aplicarDescuentoGlobalAnalisisAgregados = (porcentajeDescuento) => {
        const nuevosAnalisisAgregados = analisisAgregados.map((analisis) => {
            // 🔴 PROTECCIÓN: No modificar análisis que vienen del backend en modo editar
            if (modo === 'modificar' && analisis.descuentoDelBackend === true) {
                console.log('🛡️ PROTEGIDO:', analisis.analisis, '- Mantiene descuento del backend:', analisis.descuento);
                return analisis; // Retornar sin cambios
            }
            
            const descuento = (analisis.precio * porcentajeDescuento) / 100;
            const total = (analisis.precio - descuento) * analisis.cantidad;

            return {
                ...analisis,
                descuento: descuento.toFixed(2),
                total: total.toFixed(2),
                descuentoDelBackend: false // Marcar como recalculado
            };
        });

        setAnalisisAgregados(nuevosAnalisisAgregados);
        calcularTotales(nuevosAnalisisAgregados);
    };



    // La función recalcularDescuentosAnalisisAgregados ha sido movida a useDescuentosJerarquicos.jsx

    // Implementación de obtenerDescuentoPorAnalisis que fue movida a useDescuentosJerarquicos pero todavía se usa directamente
    const obtenerDescuentoPorAnalisis = async (idTipoAnalisis) => {
        try {
            // Si recibimos un objeto, extraemos el ID
            const analisisId = typeof idTipoAnalisis === 'object' ? idTipoAnalisis.idTipoAnalisis : idTipoAnalisis;

            const response = await fetch(`${server}/PrPromocionesDetallesDeDescuentoes/byidtipoAnalisis/${analisisId}/${sucursalSeleccionada.idSucursal}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            
            if (!response.ok) {
                if (response.status === 404) {
                    // Si no se encuentra, retornamos objeto vacío en lugar de error
                    return {};
                }
                throw new Error('Error obteniendo descuento');
            }
            
            const data = await response.json();
            
            // La API ahora devuelve un objeto directo, no un array con $values
            // Verificamos que data tenga las propiedades que necesitamos
            if (data && data.porcentaje !== undefined) {
                return {
                    idPromocion: data.idPromocion,
                    porcentaje: data.porcentaje,
                    descuentoRemitido: data.descuentoRemitido // Nueva propiedad
                };
            }
            
            // Si no hay datos válidos
            return {};
        } catch (error) {
            console.error('Error al obtener el descuento por análisis:', error);
            return {};
        }
    };
    // === Recalculo dinámico de descuentos según condiciones actuales ===
    const esPerfilCompleto = (descuentoPerfil, listaAnalisis) => {
        if (!descuentoPerfil || !descuentoPerfil.idsAnalisisPerfil) return false;
        return descuentoPerfil.idsAnalisisPerfil.every(id => listaAnalisis.some(a => a.idTipoAnalisis === id));
    };
    // Construye una firma única de las condiciones de descuento actuales.
    const buildCondicionesFirma = () => {
        return [
            clienteSeleccionado?.idCliente || '',
            origen?.idOrigen || '',
            medicoSeleccionado?.idMedico || '',
            medioPago?.id || '',
            tipoPago || '',
            isAseguradora ? '1' : '0',
            perfilDescuento?.idPerfil || '',
            descuentoGlobal,
            descuentoGlobalRemitido
        ].join('|');
    };

    const recalcularDescuentosCondiciones = async () => {
        console.log('🔴 recalcularDescuentosCondiciones INVOCADA - modo:', modo, '- analisisAgregados:', analisisAgregados.length);
        
        const firmaActual = buildCondicionesFirma();
        let perfilesValidos = [];
        let huboCambioPerfiles = false;
        descuentosAplicados.forEach(d => {
            if (d.tipo === 'Perfil') {
                if (esPerfilCompleto(d, analisisAgregados)) {
                    perfilesValidos.push(d);
                } else {
                    huboCambioPerfiles = true;
                }
            } else {
                perfilesValidos.push(d);
            }
        });
        if (huboCambioPerfiles) setDescuentosAplicados(perfilesValidos);

        console.log('🔄 Iniciando recalcularDescuentosCondiciones:', {
            analisisCount: analisisAgregados.length,
            backendFlags: analisisAgregados.map(a => ({ analisis: a.analisis, descuentoDelBackend: a.descuentoDelBackend, descuento: a.descuento }))
        });

        const nuevosAnalisis = await Promise.all(analisisAgregados.map(async a => {
            // 🔴 PRIORIDAD 1: Si el descuento viene del backend, conservar SU VALOR y mantener el flag
           
            // 🔐 1️⃣ NUNCA tocar descuentos manuales
                // if (a.descuentoManual) {
                //     return a;
                // }
              if (a.descuentoManual === true) {
                    const porcentaje = a.porcentajeManual ?? 0;

                    const descuentoMonto = (parseFloat(a.precio) * porcentaje) / 100;
                    const total = (parseFloat(a.precio) - descuentoMonto) * parseInt(a.cantidad);

                    return {
                        ...a,
                        descuento: descuentoMonto.toFixed(2),
                        total: total.toFixed(2),
                        firmaCondiciones: firmaActual
                    };
                }
           
            if (a.descuentoDelBackend) {
                console.log('⏸️ Conservando descuento del backend para:', a.analisis, '- Descuento:', a.descuento);
                // Asegurar que el flag se mantiene incluso después de recálculos
                return { ...a, descuentoDelBackend: true };
            }
            
            // Si el registro está facturado no se recalcula ni modifica.
            if (modo === 'modificar' && a.facturado) {
                return a;
            }
            // Si estamos en modo modificar y la firma no cambió, conservar valores actuales.
            if (modo === 'modificar' && a.firmaCondiciones && a.firmaCondiciones === firmaActual) {
                return a;
            }
            let porcentaje = 0;
            if (a.remitido && descuentoGlobalRemitido > 0) {
                porcentaje = descuentoGlobalRemitido;
            } else if (descuentoGlobal > 0) {
                porcentaje = descuentoGlobal;
            } else {
                if (a.idPerfil) {
                    const descPerfil = perfilesValidos.find(d => d.tipo === 'Perfil' && d.idPerfil === a.idPerfil);
                    if (descPerfil && esPerfilCompleto(descPerfil, analisisAgregados)) {
                        // Verificar que la promoción del perfil aplique al cliente por edad/sexo
                        const perfilData = perfiles.find(p => p.idPerfil === a.idPerfil);
                        if (!perfilData || esPromocionAplicableAlCliente(perfilData)) {
                            porcentaje = descPerfil.porcentaje || 0;
                        }
                    }
                }
                if (porcentaje === 0) {
                    const descAnalisis = await obtenerDescuentoPorAnalisis(a.idTipoAnalisis);
                    porcentaje = parseFloat(descAnalisis?.porcentaje) || 0;
                }
            }
            const descuentoMonto = (parseFloat(a.precio) * porcentaje) / 100;
            const total = (parseFloat(a.precio) - descuentoMonto) * parseInt(a.cantidad);
            return { 
                ...a, 
                descuento: descuentoMonto.toFixed(2), 
                total: total.toFixed(2), 
                firmaCondiciones: firmaActual, 
                descuentoDelBackend: a.descuentoDelBackend || false // 🔴 Preservar flag si ya existe
            };
        }));
        const mismos = analisisAgregados.length === nuevosAnalisis.length && analisisAgregados.every((oldA, idx) => oldA.descuento === nuevosAnalisis[idx].descuento && oldA.total === nuevosAnalisis[idx].total);
        if (!mismos) {
            setAnalisisAgregados(nuevosAnalisis);
            calcularTotales(nuevosAnalisis);
            if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
                recalcularElegiblesNoElegibles(nuevosAnalisis);
            }
        }
    };

    useEffect(() => {
        // 🔴 PROTECCIÓN CRÍTICA: En modo modificar con datos del backend, NO HACER NADA
        if (modo === 'modificar' && analisisAgregados.length > 0) {
            const todosDelBackend = analisisAgregados.every(a => a.descuentoDelBackend === true);
            console.log('🔍 CHECK useEffect:', {
                modo,
                cantidadAnalisis: analisisAgregados.length,
                todosDelBackend,
                flags: analisisAgregados.map(a => ({ analisis: a.analisis, descuentoDelBackend: a.descuentoDelBackend }))
            });
            
            if (todosDelBackend) {
                console.log('🛡️ PROTECCIÓN ACTIVA: Modo modificar + Backend data - BLOQUEADO recalcularDescuentosCondiciones');
                return;
            }
        }
        
        if (analisisAgregados.length === 0) return;
        
        console.log('🔄 Ejecutando recalcularDescuentosCondiciones - Hay análisis nuevos en modo crear');
        recalcularDescuentosCondiciones();
    }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, analisisAgregados, descuentoGlobal, descuentoGlobalRemitido]);

    // Comentado: La lógica de descuentos ahora se maneja en el componente GestorDescuentos
    // useEffect(() => {
    //     calcularDescuento();
    // }, [medicoSeleccionado, origen, clienteSeleccionado, perfil, analisisSeleccionado, fechaCotizacion]);


    const fetchClientes = async (search = '') => {
        try {
            const trimmed = (search || '').trim();
            // Evitar llamadas con búsqueda vacía que devuelven 405 en el backend
            if (!trimmed) {
                setClientes([]);
                setTotalClientes(0);
                setHasMore(false);
                return;
            }

            setIsLoading(true);

            const response = await fetch(`${server}/MgClientes/buscador/${encodeURIComponent(trimmed)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            const data = await response.json();

            if (data && data.clientes && Array.isArray(data.clientes.$values)) {
                const clientesData = data.clientes.$values;

                setClientes(clientesData);
                setTotalClientes(data.total);
                setHasMore(clientesData.length >= limit);
            } else {
                setClientes([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error al cargar los clientes:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {

    const cargarPoliza = async () => {

        if (!origen || !clienteSeleccionado) return;

        const idPoliza =
            clienteSeleccionado.idPoliza ??
            clienteSeleccionado.IdPoliza ??
            0;

        const tieneAseguradora =
            origen?.aseguradora === true ||
            origen?.aseguradora === 1;

        console.log("🟢 VALIDANDO useEffect POLIZA:", {
            origen,
            clienteSeleccionado,
            idPoliza,
            tieneAseguradora
        });

        if (!tieneAseguradora || idPoliza <= 0) return;

        try {

            const response = await fetch(
                `${server}/MgPolizasDeSeguroes/byidorigen/${origen.idOrigen}`
            );

            const data = await response.json();

            const polizaEncontrada = data.$values.find(
                (p) => p.id === idPoliza
            );

            console.log("✅ POLIZA ENCONTRADA:", polizaEncontrada);

            if (polizaEncontrada) {

                await obtenerDatosClasificacionPoliza(idPoliza, 2);

                setPoliza(polizaEncontrada.codigo || '');
                setEmpresa(polizaEncontrada.origen || '');

                setIsAseguradora(true);
            }

        } catch (error) {

            console.error("❌ Error cargando póliza:", error);

        }
    };

    cargarPoliza();

}, [origen, clienteSeleccionado]);


    const handleOrigenChange = async (e, newValue) => {
        setOrigen(newValue);
        console.log(newValue);
        if (newValue && newValue.aseguradora && clienteSeleccionado && clienteSeleccionado.idPoliza) {
            setIsAseguradora(true);
            console.log(clienteSeleccionado);
            // Validar que idPoliza no sea cero
            if (clienteSeleccionado && clienteSeleccionado.idPoliza !== 0) {
                console.log('entro al if');
                try {
                    const response = await fetch(`${server}/MgPolizasDeSeguroes/byidorigen/${newValue.idOrigen}`);
                    const data = await response.json();
                    setpolizasOrigen(data.$values);
                    // const codigoPoliza = polizas.find((p) => p.idPoliza === clienteSeleccionado.idPoliza);
                    // Busca el idPoliza en el resultado
                    const poliza = data.$values.find((p) => p.id === clienteSeleccionado.idPoliza);
                    console.log(poliza);
                    if (poliza) {
                        await obtenerDatosClasificacionPoliza(clienteSeleccionado.idPoliza, 2);
                        setPoliza(poliza.codigo);
                        setEmpresa(poliza.origen);
                    }

                    // Recalcular elegible y no elegible con los análisis actuales
                    recalcularElegiblesNoElegibles(analisisAgregados);
                } catch (error) {
                    console.error("Error al obtener los detalles de la póliza:", error);
                }
            }
        } else {
            setIsAseguradora(false);
        }
    };

    const obtenerDatosClasificacionPoliza = async (idPoliza1, idClasificacionProducto) => {
        try {
            const res = await fetch(`${server}/MgPolizasDeSeguroRangos/bypoliza/${idPoliza1}`);
            const data = await res.json();
            const rangos = data.$values || [];
            console.log(rangos);
            const rango = rangos.find(r => r.idClasificacionProducto === idClasificacionProducto);

            if (rango) {
                // Seteas aquí los valores si tenés estados definidos
                setCopago(rango.porcentajeCopago);
                setMinimo(rango.valorMinimo);
                setMaximo(rango.valorMaximo);

                // También podés retornarlo si preferís manejarlo desde donde lo llamás
                return {
                    porcentajeCopago: rango.porcentajeCopago,
                    valorMinimo: rango.valorMinimo,
                    valorMaximo: rango.valorMaximo
                };
            } else {
                // Si no se encuentra la clasificación
                setCopago(0);
                setMinimo(0);
                setMaximo(0);
                return null;
            }
        } catch (error) {
            console.error("Error al obtener el rango por clasificación:", error);
            return null;
        }
    };


    // función para verificar si la póliza del cliente se encuentra entre las pólizas del origen
    const isPolizaValidForOrigen = (clientePoliza, origenPolizas) => {
        console.log(origenPolizas);
        return clientePoliza && origenPolizas.some(poliza => poliza.id === clientePoliza);
    };

    // cuando cambian cliente o polizas, y ambos existen, entonces validamos
    useEffect(() => {
        if (cliente && polizas.length > 0) {
            const esValida = isPolizaValidForOrigen(cliente.idPoliza, polizas);
            console.log('¿Póliza válida?', esValida);
        }
    }, [cliente, polizas]);

    useEffect(() => {
        if (analisisAgregados.length === 0) return;
    
        const hayAutomaticos = analisisAgregados.some(a => !a.descuentoManual);
            if (!hayAutomaticos) return;
    
        recalcularDescuentosCondiciones();
    }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, descuentoGlobal, descuentoGlobalRemitido]);
        


    function recalcularElegiblesNoElegibles(analisisList) {
        if (!isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
            setValorElegible(0);
            setNoElegibles(0);
            setAseguradora(0);
            setValorCopago(0);
            setTotalAsegurado(0);
            return;
        }
        console.log(analisisList);
        const totalElegible = analisisList
            .filter(a => a.elegible)
            .reduce((sum, a) => sum + parseFloat(a.precio), 0);
        const totalNoElegible = analisisList
            .filter(a => !a.elegible)
            .reduce((sum, a) => sum + parseFloat(a.precio), 0);
          const descuentoNoElegible = analisisList
            .filter(a => !a.elegible)
            .reduce((sum, a) => sum + parseFloat(a.descuento), 0);
            console.log("descuento no elegible "+descuentoNoElegible);
        const valorAseguradoraBase = ((100 - copago) / 100) * totalElegible;
        const valorCopagoBase = (copago / 100) * totalElegible;

        // No aplicamos descuento al copago, solo a la aseguradora
        const descuento = parseFloat(descuentoAsegurado) || 0;
        // Distribuir primero sobre no elegibles
        // const descuentoSobreNoElegible = Math.min(descuento, totalNoElegible);
        const noElegiblesConDesc = totalNoElegible;
        const descuentoNoElegibleValue = parseFloat(descuentoNoElegible) || 0;
        const descuentoPct = (descuentoGlobal || 0) / 100;
        const deducibleValue = parseFloat(deducible) || 0;
        // const restoDescuento = descuento - descuentoSobreNoElegible;
        const valorAseguradoraConDesc = Math.max(valorAseguradoraBase - (totalElegible * descuentoPct)- deducibleValue, 0);
        

        // Total a cargo del cliente: no elegibles con descuento + copago sin descuento
        const totalAseguradoCalc = noElegiblesConDesc + valorCopagoBase - descuento + deducibleValue -descuentoNoElegibleValue ;


        setValorElegible(totalElegible.toFixed(2));
        setNoElegibles(noElegiblesConDesc.toFixed(2));
        setAseguradora(valorAseguradoraConDesc.toFixed(2));
        setValorCopago(valorCopagoBase.toFixed(2));
        setTotalAsegurado(totalAseguradoCalc.toFixed(2));
    }

    function recalcularElegiblesNoElegibles1(analisis) {
        if (!isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
            setValorElegible(0);
            setNoElegibles(0);
            setAseguradora(0);
            setValorCopago(0);
            setTotalAsegurado(0);
            return;
        }

        // 1) Sumamos precios elegibles y no elegibles
        const totalElegible = analisis
            .filter(a => a.elegible)
            .reduce((sum, curr) => sum + parseFloat(curr.precio), 0);
        const totalNoElegible = analisis
            .filter(a => !a.elegible)
            .reduce((sum, curr) => sum + parseFloat(curr.precio), 0);

        // 2) Fragmentamos el total elegible en aseguradora vs copago
        console.log(totalElegible);
        const valorAseguradoraBase = ((100 - copago) / 100) * totalElegible;
        const valorCopagoBase = (copago / 100) * totalElegible;

        // 3) Aplicamos el descuento SÓLO sobre la aseguradora
        const descuentoPct = (descuentoGlobal || 0) / 100;

        const valorAseguradoraConDesc = valorAseguradoraBase - (totalElegible * descuentoPct);
        // 4) El copago queda sin descuento
        const descuento = parseFloat(descuentoAsegurado) || 0;
        const valorCopagoFinal = valorCopagoBase - (valorCopagoBase * (descuento / 100));

        // 6) Total a cargo del asegurado = no elegibles + copago (sin descuento)
        const totalAseguradoCalc = totalNoElegible + valorCopagoFinal;

        // 5) Seteamos los estados
        setValorElegible(totalElegible.toFixed(2));
        setNoElegibles(totalNoElegible.toFixed(2));
        setAseguradora(valorAseguradoraConDesc.toFixed(2));
        setValorCopago(valorCopagoFinal.toFixed(2));


        setTotalAsegurado(totalAseguradoCalc.toFixed(2));

        const totalPagarCalc = valorAseguradoraConDesc + totalAseguradoCalc;
        setTotalPagar(totalPagarCalc.toFixed(2));

        // Si aún quieres recalcular descuentoTotal y descuentoTotalP:
        const valorVentaCalc = totalElegible + totalNoElegible;
        const descuentoTotalCalc = valorVentaCalc - totalPagarCalc;
        const descuentoTotalPctCalc = valorVentaCalc > 0
            ? (descuentoTotalCalc / valorVentaCalc) * 100
            : 0;

        setValorVenta(valorVentaCalc.toFixed(2));
        setDescuentoTotal(descuentoTotalCalc.toFixed(2));
        setDescuentoTotalP(descuentoTotalPctCalc.toFixed(2));
    }


    // Llama a la función `recalcularElegiblesNoElegibles` cada vez que se agrega un análisis
    useEffect(() => {
        if (poliza) {
            recalcularElegiblesNoElegibles(analisisAgregados);
        }
    }, [analisisAgregados, descuentoAsegurado, deducible]);

    const handleElegibleChange = (index) => {
        setAnalisisAgregados((prevAnalisis) => {
            const updatedAnalisis = prevAnalisis.map((analisis, i) => {
                if (i === index) {
                    return { ...analisis, elegible: !analisis.elegible };
                }
                return analisis;
            });

            if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
                recalcularElegiblesNoElegibles(updatedAnalisis);
            }
            return updatedAnalisis;
        });

    };

    // Función para obtener los detalles de la cotización
    const obtenerDetallesOrdenTrabajo = async (idOrdenTrabajo) => {
        try {
            const response = await fetch(`${server}/MgOrdenesMedicasDetalles/idOrdenTrabajo/${idOrdenTrabajo}`);
            const data = await response.json();
            if (data && data.$values) {
                const firmaCarga = buildCondicionesFirma();
                const detallesAnalisis = data.$values.map((detalle) => ({
                    idDetalle: detalle.idDetalle,
                    idOrdenMedica: detalle.idOrdenMedica,
                    idModulo: detalle.idModulo,
                    nombreModulo: detalle.nombreModulo,
                    idPerfil: 0,
                    perfil: '',
                    idTipoAnalisis: detalle.idTipoAnalisis,
                    analisis: detalle.descripcionTipoAnalisis,
                    cantidad: 1,
                    idTipoPrecio: detalle.idTipoPrecio,
                    precio: detalle.valorVenta,
                    descuento: detalle.valorDescuento,
                    total: (parseFloat(detalle.valorVenta) - parseFloat(detalle.valorDescuento)) * 1,
                    elegible: detalle.polizaElegible || false,
                    comentarios: detalle.comentarios || '', // Agregar comentarios
                    idFactura: detalle.idFactura || 0, // 🔴 ID de factura asociada
                    facturado: !!detalle.facturado, // Nuevo: indica si ya fue facturado
                    remitido: !!detalle.remitido,   // Preservar estado de remitido si viene del backend
                    descuentoOriginal: detalle.valorDescuento, // Almacena descuento inicial
                    descuentoDelBackend: true, // 🔴 Flag para indicar que este descuento viene del backend y NO debe recalcularse
                    firmaCondiciones: firmaCarga, // Snapshot de condiciones al cargar
                    bloqueado: !!detalle.facturado, // Alias para facilidad en UI
                    // En este ejemplo usamos idDetalle para identificar cada registro
                }));
                setAnalisisAgregados(detallesAnalisis);
                // Guardamos también el arreglo original para la comparación
                setAnalisisOriginal(detallesAnalisis);
                calcularTotales(detallesAnalisis);
                
                
                console.log('📦 CARGAR ORDEN EN EDITAR - Detalles del backend:', {
                    cantidad: detallesAnalisis.length,
                    modo: 'modificar',
                    idOrden: ordenTrabajo.idOrdenMedica,
                    detalles: detallesAnalisis.map(d => ({ 
                        analisis: d.analisis, 
                        descuentoMonto: d.descuento,
                        descuentoDelBackend: d.descuentoDelBackend,
                        precio: d.precio
                    }))
                });
                
                console.log('✅ SETEO DE analisisAgregados COMPLETADO - Esperando que useEffect no recalcule');
            }
        } catch (error) {
            console.error("Error al obtener los detalles de la cotización:", error);
        }
    };

    useEffect(() => {
        if (ordenTrabajo) {
            console.log('Entraron aqui', ordenTrabajo, modo);
            if (modo === 'dividir') {
                setFechaCotizacion(ordenTrabajo.fechaCotizacion || '');

                setFechaEntrega(ordenTrabajo.fechaEntrega || '');
                setValidez(ordenTrabajo.duracion || 1);
                setMedidaT(ordenTrabajo.idTipoDuracion || '');
                setTipoPago(ordenTrabajo.idTipoPago || '');
                setMedicoSeleccionado(ordenTrabajo.idMedico || null);
                setOrigen(ordenTrabajo.idOrigen || null);
                // Buscar el medio de pago completo en la lista usando idMedioPago
                console.log('Modo dividir - buscando medio de pago con id:', ordenTrabajo.idMedioPago);
                const medioPagoCompleto = mediosPago.find(m => m.id === ordenTrabajo.idMedioPago);
                console.log('Medio de pago encontrado para dividir:', medioPagoCompleto);
                setMedioPago(medioPagoCompleto || null);
                setObservaciones(ordenTrabajo.observaciones || '');
                setWhatsapp(ordenTrabajo.enviarXWhatsapp || false);
                setCorreo(ordenTrabajo.enviarXCorreo || false);
                setApp(ordenTrabajo.enviarXApp || false);
                setImpresos(ordenTrabajo.imprimir || false);
                setMedicoSeleccionado(ordenTrabajo.idMedico || null);
                setOrigen(ordenTrabajo.idOrigen || null);
                fetchComentarios(ordenTrabajo.idOrdenMedica);
                // setAnalisisAgregados(detalles || []);
                setIsEditing(false); // Indicador de modo edición
                // Busca el cliente en la lista de clientes


            } else if (modo === 'modificar') {
                console.log('Entro en Modificar', ordenTrabajo);
                setFechaCotizacion(ordenTrabajo.fechaCotizacion || '');
                setFechaRecepcion(ordenTrabajo.fechaRecepcion || '');

                      const tipoTiempoGuardado = tipoTiempoOptions.find(t => t.value === ordenTrabajo.tipoTiempo);
            setTipoTiempoSeleccionado(ordenTrabajo.tipoTiempo || '');
            
            // Mantener los datos guardados para Tiempo
            setTiempo(ordenTrabajo.tiempo || '');
            
            if (ordenTrabajo.fechaEntrega) {
                const fechaRaw = ordenTrabajo.fechaEntrega;
                const formattedEntrega = fechaRaw.includes('T')
                    ? fechaRaw
                    : fechaRaw.replace(' ', 'T').slice(0, 16); // Asegura formato yyyy-MM-ddTHH:mm
                setFechaEntrega(formattedEntrega);
            }
                setIsModificable(true);

                setFechaEntrega(ordenTrabajo.fechaEntrega || '');
                setValidez(ordenTrabajo.duracion || 1);
                setMedidaT(ordenTrabajo.idTipoDuracion || '');
                setTipoPago(ordenTrabajo.idTipoPago ? ordenTrabajo.idTipoPago : 1 || '');
                setMedicoSeleccionado(ordenTrabajo.idMedico || null);
                setOrigen(ordenTrabajo.idOrigen || null);
                // Buscar el medio de pago completo en la lista usando idMedioPago
                console.log('Modo modificar - buscando medio de pago con id:', ordenTrabajo.idMedioPago);
                const medioPagoCompleto = mediosPago.find(m => m.id === ordenTrabajo.idMedioPago);
                console.log('Medio de pago encontrado para modificar:', medioPagoCompleto);
                setMedioPago(medioPagoCompleto || null);
                setObservaciones(ordenTrabajo.observaciones || '');
                setWhatsapp(ordenTrabajo.enviarXWhatsapp || false);
                setCorreo(ordenTrabajo.enviarXCorreo || false);
                setApp(ordenTrabajo.enviarXApp || false);
                setImpresos(ordenTrabajo.imprimir || false);
                // Buscar el médico completo en la lista
            const medicoCompleto = Medicos.find(m => m.idMedico === ordenTrabajo.idMedico);
            setMedicoSeleccionado(medicoCompleto || null);
            
            // Buscar el origen completo en la lista
            const origenCompleto = origenes.find(o => o.idOrigen === ordenTrabajo.idOrigen);
            setOrigen(origenCompleto || null);

                fetchComentarios(ordenTrabajo.idOrdenMedica);
              obtenerDatosPolizaGuardada(ordenTrabajo.idOrdenMedica);

                // setAnalisisAgregados(detalles || []);
                setIsEditing(true); // Indicador de modo edición

            }

            const cliente = clientes.find(c => c.idCliente === ordenTrabajo.idCliente);
            if (cliente) {
                console.log('Cliente encontrado:', cliente);
                setClienteSeleccionado(cliente);
                setInputValue(cliente.nombreCompleto);
                // Llama a handleClienteSeleccionado para llenar otros campos
                handleClienteSeleccionado(null, cliente);
            } else {
                // Si el cliente no está en la lista, lo obtienes desde la API
                fetch(`${server}/MgClientes/GetByIdCliente/${ordenTrabajo.idCliente}`)
                    .then(response => response.json())
                    .then(data => {
                        console.log(data);
                        setClienteSeleccionado(data);
                        setInputValue(data.nombreCompleto);
                        handleClienteSeleccionado(null, data);
                    })
                    .catch(error => {
                        console.error('Error al obtener el cliente:', error);
                    });
            }


            console.log(ordenTrabajo);
            obtenerDetallesOrdenTrabajo(ordenTrabajo.idOrdenMedica);


        } else if (cliente) {
            // Nuevo caso: se ha proporcionado un cliente sin una orden de trabajo
            console.log(cliente);
            fetch(`${server}/MgClientes/GetByIdCliente/${cliente.idCliente}`)
                .then(response => response.json())
                .then(data => {
                    console.log(data);
                    setClienteSeleccionado(data);
                    setInputValue(data.nombreCompleto);
                    handleClienteSeleccionado(null, data);
                })
                .catch(error => {
                    console.error('Error al obtener el cliente:', error);
                });
            // setClienteSeleccionado(cliente);
            setInputValue(cliente.nombreCompleto);
            isPolizaValidForOrigen(cliente.idPoliza, polizas);
            // Inicializar otros estados con la información del cliente
            setIdentidad(cliente.numeroIdentidad);
            const fechaNacimiento = new Date(cliente.fechaNacimiento);
            const hoy = new Date();
            let edadAnios = hoy.getFullYear() - fechaNacimiento.getFullYear();
            let edadMeses = hoy.getMonth() - fechaNacimiento.getMonth();

            if (edadMeses < 0) {
                edadAnios--;
                edadMeses += 12;
            }

            const edadCalculada = `${edadAnios} años y ${edadMeses} meses`;
            setEdad(edadCalculada);
            setSexo(cliente.idSexo);
            setDireccion(cliente.direccionCompleta);
            setTelefono(cliente.celular);
            setCorreoElectronico(cliente.correoElectronico);

            // Inicializar otros estados según necesites
            // Por ejemplo, fechas, sucursal, etc.
            setFechaCotizacion(new Date().toISOString().split('T')[0]); // Fecha actual en formato YYYY-MM-DD
            setValidez(1);
            setMedidaT('');
            setTipoPago('');
            setWhatsapp(false);
            setApp(false);
            setCorreo(false);
            setImpresos(false);
            setMedicoSeleccionado(null);
            setOrigen(null);
            // Establecer EFECTIVO (id=0) como valor por defecto
            console.log('Inicializando con cliente - buscando EFECTIVO...');
            const efectivo = mediosPago.find(m => m.id === 0);
            console.log('EFECTIVO encontrado en inicialización:', efectivo);
            setMedioPago(efectivo || null);
            setObservaciones('');
            setPerfil(null);
            setAnalisisSeleccionado(null);
            setAnalisisAgregados([]);
            setCantidad(1);
            setValor(0);
            setDescuento(0);
            setTotalPagar(0);
            setDescuentoTotal(0);
            setDescuentoTotalP(0);
            setCantidadTotal(0);
            setDescuentoGlobal(0);
            // ... otros estados necesarios
        } else {

            // Si no hay cotización, inicializamos los estados a sus valores por defecto
            setClienteSeleccionado(null);
            setFechaCotizacion('');
            setValidez(1);
            setMedidaT('');
            setTipoPago('');
            setMedicoSeleccionado(null);
            setOrigen(null);
            // Establecer EFECTIVO (id=0) como valor por defecto
            console.log('Inicializando sin orden/cliente - buscando EFECTIVO...');
            const efectivo = mediosPago.find(m => m.id === 0);
            console.log('EFECTIVO encontrado en inicialización vacía:', efectivo);
            setMedioPago(efectivo || null);
            setObservaciones('');
            setWhatsapp(false);
            setApp(false);
            setCorreo(false);
            setImpresos(false);
            setAnalisisAgregados([]);
            setIsEditing(false); // Modo creación
        }
            if (origen && origen.aseguradora && clienteSeleccionado && clienteSeleccionado.idPoliza) {
        setIsAseguradora(true);
        console.log('Cliente y origen disponibles:', clienteSeleccionado);
        
        // Validar que idPoliza no sea cero
        if (clienteSeleccionado.idPoliza !== 0) {
            console.log('Procesando póliza para origen aseguradora');
            
            // Ejecutar la misma lógica que en handleOrigenChange
            fetch(`${server}/MgPolizasDeSeguroes/byidorigen/${origen.idOrigen}`)
                .then(response => response.json())
                .then(data => {
                    setpolizasOrigen(data.$values);
                    const poliza = data.$values.find((p) => p.id === clienteSeleccionado.idPoliza);
                    console.log('Póliza encontrada:', poliza);
                    
                    if (poliza) {
                        obtenerDatosClasificacionPoliza(clienteSeleccionado.idPoliza, 2);
                        setPoliza(poliza.codigo);
                        setEmpresa(poliza.origen);
                    }
                    
                    // Recalcular elegible y no elegible con los análisis actuales
                    if (analisisAgregados.length > 0) {
                        recalcularElegiblesNoElegibles(analisisAgregados);
                    }
                })
                .catch(error => {
                    console.error("Error al obtener los detalles de la póliza:", error);
                });
        }
    } else if (origen && !origen.aseguradora) {
        // Si el origen no es aseguradora, limpiar el estado
        setIsAseguradora(false);
    }
    }, [ordenTrabajo, modo, Medicos, origenes, mediosPago]);


    useEffect(() => {
        const now = new Date();

        const currentYear = now.getFullYear();
        const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
        const currentDay = String(now.getDate()).padStart(2, '0');
        const fechaActual = `${currentYear}-${currentMonth}-${currentDay}`;

        const currentHours = String(now.getHours()).padStart(2, '0');
        const currentMinutes = String(now.getMinutes()).padStart(2, '0');
        const fechaHoraActual = `${fechaActual}T${currentHours}:${currentMinutes}`;

        setFechaCotizacion(fechaActual);
        setFechaRecepcion(fechaHoraActual);
        // setFechaEntrega(''); // 🧼 Deja la fecha de entrega vacía al inicio
    }, []);

    // Fetch de datos desde las APIs para llenar los autocomplete y selects
    useEffect(() => {
        fetchClientes(searchQuery);
        const fetchSexo = async () => {
            try {
                const response = await fetch(`${server}/MgSexoes`);
                const data = await response.json();
                setSexos(data.$values);
            } catch (error) {
                console.error('Error al cargar las sucursales:', error);
            }
        };

        const fetchSucursales = async () => {
            try {
                const response = await fetch(`${server}/MgSucursales`);
                const data = await response.json();
                setSucursales(data.$values);
            } catch (error) {
                console.error('Error al cargar las sucursales:', error);
            }
        };

        const fetchMedicos = async () => {
            try {
                const response = await fetch(`${server}/MgMedicoes`);
                const data = await response.json();
                setMedicos(data.$values);
                console.log('Medicos', data.$values);
            } catch (error) {
                console.error('Error al cargar los médicos:', error);
            }
        };

        const fetchOrigen = async () => {
            try {
                const response = await fetch(`${server}/MgOrigenes`);
                const data = await response.json();
                setOrigenes(data.$values);
            } catch (error) {
                console.error('Error al cargar los orígenes:', error);
            }
        };

        const fetchMedioPago = async () => {
            try {
                const response = await fetch(`${server}/MgTarjetasBancarias`);
                const data = await response.json();
                console.log('Medios de pago cargados:', data.$values);
                setMediosPago(data.$values);
            } catch (error) {
                console.error('Error al cargar los medios de pago:', error);
            }
        };

        const fetchPerfiles = async () => {
            try {
                        const idSucursal = sucursalSeleccionada?.idSucursal;

                const response = await fetch(`${server}/MgAnalisisGeneralPerfiles/vigentesporsucursal/${idSucursal}`);
                console.log(`${server}/MgAnalisisGeneralPerfiles/vigentesporsucursal/${idSucursal}`);
                const data = await response.json();
                setPerfiles(data.$values);
                console.log('perfiles', data.$values);
            } catch (error) {
                console.error('Error al cargar los perfiles:', error);
            }
        };

        const fetchAnalisis = async () => {
            try {
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis`);
                const data = await response.json();
                setAnalisis(data.$values);
                console.log(data.$values);
            } catch (error) {
                console.error('Error al cargar los tipos de analisis:', error);
            }
        };




        const fetchTiposCotizacion = async () => {
            try {
                const response = await fetch(`${server}/CtzCotizacionesTipoes`);
                const data = await response.json();
                setTiposCotizacion(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };


        const fetchTiposPagos = async () => {
            try {
                const response = await fetch(`${server}/CtzTiposDePagoes`);
                const data = await response.json();
                setTiposPagos(data.$values);
                console.log(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchTiposPrecio = async () => {
            try {
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisTiposDePrecios`);
                const data = await response.json();
                setTiposPrecio(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchPrioridadesDescuento = async () => {
            try {
                const response = await fetch(`${server}/conf_prioridad_descuento`);
                const data = await response.json();
                setPrioridadDescuento(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchMedidasTipos = async () => {
            try {
                const response = await fetch(`${server}/CtzTipoDuracions`);
                const data = await response.json();
                setMedidasTiempo(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchPolizas = async () => {
            try {
                const response = await fetch(`${server}/MgPolizasDeSeguroes`);
                const data = await response.json();
                setPolizas(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        // Llamar las funciones para obtener los datos
        // fetchClientes(); // ⚠️ Comentado: no cargar clientes al inicio, solo cuando usuario escriba en Autocomplete
        fetchSucursales();
        fetchMedicos();
        fetchOrigen();
        fetchMedioPago();
        fetchPerfiles();
        fetchAnalisis();
        fetchSexo();
        fetchTiposCotizacion();
        fetchTiposPagos();
        fetchMedidasTipos();
        fetchTiposPrecio();
        fetchPrioridadesDescuento();
        fetchPolizas();
    }, []);

    // Funciones para manejar la paginación de la tabla
    const handleChangeTablePage = (event, newPage) => {
        setTablePage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setTablePage(0);
    };


    const handleOpenModalCliente = () => {
        if (!clienteSeleccionado) {
            alert('Por favor, selecciona un cliente para editar.');
            return;
        }
        setOpenModalCliente(true);
    };

    const handleCloseModalCliente = () => {
        setOpenModalCliente(false);
    };


    const handleClienteActualizado = async () => {
        // Volver a obtener la información del cliente actualizado
        console.log(clienteSeleccionado.idCliente);
        if (clienteSeleccionado) {
            try {
                const response = await fetch(`${server}/MgClientes/GetByIdCliente/${clienteSeleccionado.idCliente}`);
                const data = await response.json();
                
                // Actualizar el cliente seleccionado
                setClienteSeleccionado(data);
                
                // Actualizar todos los campos individuales con los nuevos datos
                setIdentidad(data.numeroIdentidad || '');
                
                // Calcular la edad actualizada
                if (data.fechaNacimiento) {
                    const fechaNacimiento = new Date(data.fechaNacimiento);
                    const hoy = new Date();
                    let edadAnios = hoy.getFullYear() - fechaNacimiento.getFullYear();
                    let edadMeses = hoy.getMonth() - fechaNacimiento.getMonth();

                    if (edadMeses < 0) {
                        edadAnios--;
                        edadMeses += 12;
                    }

                    const edadCalculada = `${edadAnios} años y ${edadMeses} meses`;
                    setEdad(edadCalculada);
                } else {
                    setEdad('');
                }
                
                setSexo(data.idSexo || '');
                setDireccion(data.direccionCompleta || '');
                setTelefono(data.celular || '');
                setCorreoElectronico(data.correoElectronico || '');
                
                
                console.log('✅ Cliente actualizado con nuevos datos:', data);
            } catch (error) {
                console.error('Error al actualizar la información del cliente:', error);
            }
        }
        // Cerrar el modal
        handleCloseModalCliente();
    };


    // Función para calcular totales
    const calcularTotales = (analisisArray) => {
        let valorVentaTotal = 0;
        let descuentoTotalSuma = 0;
        let totalPagarSuma = 0;
        let totalanalsis = 0;

        analisisArray.forEach((analisis) => {
            valorVentaTotal += parseFloat(analisis.precio) * parseInt(analisis.cantidad);
            descuentoTotalSuma += parseFloat(analisis.descuento) * parseInt(analisis.cantidad);
            totalPagarSuma += parseFloat(analisis.total);
            totalanalsis += parseInt(analisis.cantidad);
        });

        const descuentoTotalPorcentaje =
            valorVentaTotal > 0 ? (descuentoTotalSuma / valorVentaTotal) * 100 : 0;

        setValorVenta(valorVentaTotal.toFixed(2));
        setDescuentoTotal(descuentoTotalSuma.toFixed(2));
        setDescuentoTotalP(descuentoTotalPorcentaje.toFixed(2));
        setTotalPagar(totalPagarSuma.toFixed(2));
        setCantidadTotal(totalanalsis);
    };

    const handleAgregarAnalisis = async () => {
        if (isAdding) return; // Previene doble ejecución rápida
        if (!analisisSeleccionado) {
            Swal.fire({
                text: "Por favor, selecciona un análisis.",
                icon: "warning"
            });
            return;
        }

        if (!clienteSeleccionado) {
            Swal.fire({
                text: "Por favor, selecciona un cliente antes de agregar un análisis.",
                icon: "warning"
            });
            return;
        }

   if (analisisAgregados.some(a => a.idTipoAnalisis === analisisSeleccionado.idTipoAnalisis)) {
    Swal.fire({
        text: "El análisis ya ha sido agregado. No se pueden duplicar.",
        icon: "warning",
        confirmButtonText: "OK",
        allowEnterKey: true,
        allowEscapeKey: true,
        focusConfirm: true,
        buttonsStyling: true
    });
    return;
}
        setIsAdding(true); // 🔐 Bloquea ejecución múltiple
        const valorNumerico = parseFloat(valor) || 0;
        const cantidadNumerica = parseInt(cantidad) || 1;

        // 🔴 VALIDACIÓN CRÍTICA: No permitir análisis con precio cero
        if (valorNumerico <= 0) {
            Swal.fire({
                icon: 'error',
                title: 'Precio inválido',
                text: 'No se puede agregar un análisis con precio cero. Verifique que el análisis tenga un precio asignado para esta sucursal.',
            });
            setIsAdding(false);
            return;
        }

        // Obtener descuento y idPromocion dependiendo del descuentoGlobal
        let porcentajeDescuento = 0;
        let idPromocionAplicada = null;

        // Si el usuario  borra o pone 0, volver a aplicar descuento automatico
       if (descuentoManualRef.current) {
         // ✅ DESCUENTO MANUAL
        porcentajeDescuento = parseFloat(descuento) || 0;
    } else {
        if (analisisSeleccionado.remitido === 1 && descuentoGlobalRemitido > 0) {
            porcentajeDescuento = descuentoGlobalRemitido;
            idPromocionAplicada = null;
        }

        else if (descuentoGlobal > 0) {
            porcentajeDescuento = descuentoGlobal;
        } else {
            const resultadoDescuento = await obtenerDescuentoPorAnalisis(analisisSeleccionado);
            console.log('resultadoDescuento:', resultadoDescuento);
            porcentajeDescuento = parseFloat(resultadoDescuento?.porcentaje) || 0;
            idPromocionAplicada = resultadoDescuento?.idPromocion || null;
        }
    }
        const descuentoMonto = (valorNumerico * porcentajeDescuento) / 100;
        const total = (valorNumerico - descuentoMonto) * cantidadNumerica;

        const nuevoAnalisis = {
            idModulo: analisisSeleccionado.idModulo,
            idPerfil: 0,
            perfil: '',
            idTipoAnalisis: analisisSeleccionado.idTipoAnalisis,
            analisis: analisisSeleccionado.descripcionTipoAnalisis,
            cantidad: cantidadNumerica,
            idTipoPrecio: tipoPrecio,
            precio: valorNumerico,

            descuentoManual: descuentoManualRef.current, // 🔐 CLAVE
            porcentajeManual: descuentoManualRef.current ? porcentajeDescuento : null,
            descuento: descuentoMonto.toFixed(2),
            total: total.toFixed(2),

            elegible: elegible,
            comentarios: comentarios,
            idPromocion: idPromocionAplicada,
            remitido: analisisSeleccionado.remitido
        };

        setAnalisisAgregados((prevAnalisis) => {
            const updatedAnalisis = [...prevAnalisis, nuevoAnalisis];
            calcularTotales(updatedAnalisis);
            return updatedAnalisis;
        });

        // Llama al cálculo si es válido según la póliza
        if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
            recalcularElegiblesNoElegibles([...analisisAgregados, nuevoAnalisis]);
        }

        // Limpieza de campos después de agregar
        descuentoManualRef.current = false; // reset manual
        setAnalisisSeleccionado(null);
        setInputAnalisisValue('');  // Limpiar también el inputValue del Autocomplete
        setValor(0);
        setCantidad(1);
        setDescuento(0);
        setComentarios('');
        setIsAdding(false); // 🔓 Libera ejecución
        setTimeout(() => recalcularDescuentosCondiciones(), 0);
    };

    const handleAgregarAnalisisPerfil = async () => {

        if (!clienteSeleccionado) {
            Swal.fire({
                text: "Por favor, selecciona un cliente antes de agregar un análisis.",
                icon: "warning"
            });
            return;
        }

        if (!perfil) {
            Swal.fire({
                text: "Por favor, selecciona un perfil.",
                icon: "warning"
            });
            return;
        }

        try {
            const response = await fetch(`${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisisbyPerfilbySucursal/${perfil.idPerfil}/${sucursalSeleccionada.idSucursal}`);
            const data = await response.json();

            if (data && data.$values && Array.isArray(data.$values)) {
                const idsAnalisisPerfil = data.$values.map(a => a.idTipoAnalisis);
                
                // Validar si algún análisis del perfil ya está agregado
                const idsAnalisisYaAgregados = analisisAgregados.map(a => a.idTipoAnalisis);
                const analisisDuplicados = data.$values.filter(analisisItem => 
                    idsAnalisisYaAgregados.includes(analisisItem.idTipoAnalisis)
                );
                
                if (analisisDuplicados.length > 0) {
                    const nombresAnalisisDuplicados = analisisDuplicados.map(a => a.descripcionTipoAnalisis).join(', ');
                    Swal.fire({
                        title: "Análisis Duplicados",
                        text: `Los siguientes análisis del perfil ya están agregados: ${nombresAnalisisDuplicados}. No se pueden duplicar análisis.`,
                        icon: "warning",
                        confirmButtonText: "OK",
                        allowEnterKey: true,
                        allowEscapeKey: true,
                        focusConfirm: true,
                        buttonsStyling: true
                    });
                    return;
                }

                // Obtener el descuento por perfil directamente del objeto
                let porcentajeDescuentoPerfil = 0;
                let descuentoRemitidoPerfil = 0;
                let idPromocionPerfil = null;
                
                if (descuentoGlobal === 0) {
                    // Verificar si la promoción aplica al cliente por edad y sexo
                    if (esPromocionAplicableAlCliente(perfil)) {
                        porcentajeDescuentoPerfil = perfil?.porcentaje || 0;
                        descuentoRemitidoPerfil = perfil?.descuento_remitido || 0;
                        idPromocionPerfil = perfil?.idPromocion || null;
                    } else if (perfil?.porcentaje > 0) {
                        console.log('⚠️ La promoción del perfil no aplica al cliente por restricción de edad/sexo');
                    }
                }
                
                console.log('Datos de descuento del perfil:', {
                    porcentajeNormal: porcentajeDescuentoPerfil,
                    porcentajeRemitido: descuentoRemitidoPerfil,
                    idPromocion: idPromocionPerfil
                });
                // 🔴 VALIDACIÓN CRÍTICA: Filtrar análisis con precio cero del perfil
                const analisisSinPrecio = data.$values.filter(a => !a.precio || parseFloat(a.precio) <= 0);
                if (analisisSinPrecio.length > 0) {
                    const nombresAnalisis = analisisSinPrecio.map(a => a.descripcionTipoAnalisis || 'Desconocido').join(', ');
                    await Swal.fire({
                        icon: 'error',
                        title: 'Análisis sin precio',
                        html: `Los siguientes análisis del perfil tienen precio cero y no pueden ser agregados:<br><br><strong>${nombresAnalisis}</strong><br><br>Verifique que todos los análisis del perfil tengan precio asignado para esta sucursal.`,
                    });
                    return;
                }

                const nuevosAnalisis = data.$values.map((analisisItem) => {
                    let porcentajeDescuento = porcentajeDescuentoPerfil;

                    // Si hay descuentoGlobal, este tiene prioridad
                    if (descuentoGlobal > 0) {
                        porcentajeDescuento = descuentoGlobal;
                    }

                    const precio = parseFloat(analisisItem.precio);
                    const descuentoMonto = (precio * porcentajeDescuento) / 100;
                    const total = (precio - descuentoMonto) * 1; // cantidad = 1

                    return {
                        idPerfil: perfil.idPerfil,
                        perfil: perfil.descripcionPerfil,
                        idTipoAnalisis: analisisItem.idTipoAnalisis,
                        analisis: analisisItem.descripcionTipoAnalisis,
                        cantidad: 1,
                        idTipoPrecio: analisisItem.idTipoPrecio,
                        precio: precio,
                        descripcionTipoPrecio: analisisItem.descripcionTipoPrecio,
                        idModulo: analisisItem.idModulo,
                        descuento: descuentoMonto.toFixed(2),
                        total: total.toFixed(2),
                        elegible: false,
                        comentarios: '',
                        idPromocion: idPromocionPerfil,
                        // Asegurar que se identifica como parte del perfil para descuentos específicos
                        esDePerfil: true
                    };
                });

                // Solo agregar el descuento del perfil si descuentoGlobal es 0 y el porcentaje de descuento del perfil es mayor que 0
                if (descuentoGlobal === 0 && porcentajeDescuentoPerfil > 0) {
                    setDescuentosAplicados((prevDescuentos) => [
                        ...prevDescuentos,
                        {
                            tipo: 'Perfil',
                            porcentaje: porcentajeDescuentoPerfil,
                            idPerfil: perfil.idPerfil,
                            idsAnalisisPerfil: idsAnalisisPerfil,
                            idPromocion: idPromocionPerfil
                        },
                    ]);
                }

                setAnalisisAgregados((prevAnalisis) => {
                    const updatedAnalisis = [...prevAnalisis, ...nuevosAnalisis];
                    calcularTotales(updatedAnalisis);
                    return updatedAnalisis;
                });

                if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
                    recalcularElegiblesNoElegibles([...analisisAgregados, ...nuevosAnalisis]);
                }

                // Mantener el perfil seleccionado para que los descuentos se apliquen correctamente
                setPerfilDescuento(perfil);
            setPerfil(null);
                console.log('✅ Manteniendo perfil seleccionado para aplicar descuentos:', perfil);
            } else {
                alert('No se encontraron análisis para el perfil seleccionado.');
            }
        } catch (error) {
            console.error('Error al cargar los análisis del perfil:', error);
            // alert('Hubo un error al cargar los análisis del perfil.');
        }
    };


const handleEliminarAnalisis = async (index) => {
    // Validar que el análisis no esté facturado SOLO en modo modificar
    if (modo === 'modificar') {
        const analisisAEliminar = analisisAgregados[index];
        console.log('🗑️ Intentando eliminar análisis:', {
            modo,
            analisis: analisisAEliminar,
            idFactura: analisisAEliminar?.idFactura,
            tieneFactura: analisisAEliminar && analisisAEliminar.idFactura && analisisAEliminar.idFactura !== 0
        });
        
        if (analisisAEliminar && analisisAEliminar.idFactura && analisisAEliminar.idFactura !== 0) {
            swalWithHighZIndex.fire({
                icon: 'error',
                title: 'No se puede eliminar',
                text: 'Este análisis ya está facturado y no puede ser eliminado.',
                confirmButtonText: 'Entendido'
            });
            return;
        }
    }

    // Eliminar el análisis seleccionado
    const updatedAnalisis = analisisAgregados.filter((_, i) => i !== index);

    // Actualizar los descuentos
    const nuevosDescuentosAplicados = descuentosAplicados.filter((descuento) => {
        if (descuento.tipo === 'Perfil') {
            const idsAnalisisAgregados = updatedAnalisis.map((a) => a.idTipoAnalisis);
            const perfilCompleto = descuento.idsAnalisisPerfil.every((idAnalisis) =>
                idsAnalisisAgregados.includes(idAnalisis)
            );

            if (!perfilCompleto) {
                // Si el perfil ya no está completo, eliminar el descuento de ese perfil
                // y actualizar los análisis que pertenecían al perfil
                updatedAnalisis.forEach((analisis) => {
                    if (descuento.idsAnalisisPerfil.includes(analisis.idTipoAnalisis)) {
                        analisis.idPerfil = 0;
                        analisis.perfil = '';
                    }
                });
            }

            return perfilCompleto; // Mantener el descuento si el perfil está completo
        }
        return true; // Mantener otros descuentos
    });

    // En lugar de usar recalcularDescuentosAnalisisAgregados, simplificar:
    setAnalisisAgregados(updatedAnalisis);
    setDescuentosAplicados(nuevosDescuentosAplicados);
    calcularTotales(updatedAnalisis);
    
    // Recalcular elegibles si hay póliza
    if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
        recalcularElegiblesNoElegibles(updatedAnalisis);
    }
    
    // 🔴 PROTECCIÓN: No recalcular en modo modificar si todos los análisis son del backend
    if (modo === 'modificar' && updatedAnalisis.every(a => a.descuentoDelBackend === true)) {
        console.log('🛡️ PROTECCIÓN handleEliminarAnalisis: Modo modificar + Backend data - BLOQUEADO recalcularDescuentosCondiciones');
        return;
    }
    
    setTimeout(() => recalcularDescuentosCondiciones(), 0);
};

    // Funciones para manejar la edición de comentarios
    const handleAbrirModalEditarComentarios = async (analisis, index) => {
        setAnalisisParaEditar({ ...analisis, index });
        setComentarioEditando(analisis.comentarios || '');
        setTipoPrecioEditando(analisis.idTipoPrecio || '');
        setElegibleEditando(analisis.elegible || false);
        // setPrecioEditando(parseFloat(analisis.precio) || 0);
        // setDescuentoEditando(parseFloat(analisis.descuento) || 0);
        const precio = parseFloat(analisis.precio) || 0;
        const descuentoMonto = parseFloat(analisis.descuento) || 0;

        setPrecioEditando(precio);
        setDescuentoEditando(descuentoMonto);
        
        // ✅ Inicializar porcentaje de descuento a partir del monto
        setDescuentoPorcentajeEditando(
            precio > 0 ? (descuentoMonto / precio) * 100 : 0
        );  

        // Cargar tipos de precio disponibles para este análisis
        try {
            const url = `${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisispreciobySucursal/${analisis.idTipoAnalisis}/${sucursalSeleccionada.idSucursal}`;
            const response = await fetch(url);
            if (response.ok) {
                const apiResult = await response.json();
                const preciosData = apiResult.$values || apiResult;
                const tiposPrecioDisponibles = preciosData.map(precio => ({
                    idTipoPrecio: precio.idTipoPrecio,
                    descripcionTipoPrecio: precio.descripcionTipoPrecio,
                    precio: precio.precio
                })).sort((a, b) => {
                    if (a.descripcionTipoPrecio === 'CONSULTA EXTERNA') return -1;
                    if (b.descripcionTipoPrecio === 'CONSULTA EXTERNA') return 1;
                    return 0;
                });
                setTiposPrecioAnalisisEdicion(tiposPrecioDisponibles);
            }
        } catch (error) {
            console.error('Error cargando tipos de precio:', error);
            setTiposPrecioAnalisisEdicion([]);
        }
        
        setOpenModalEditarComentarios(true);
    };

    const handleCerrarModalEditarComentarios = () => {
        setOpenModalEditarComentarios(false);
        setAnalisisParaEditar(null);
        setComentarioEditando('');
        setTipoPrecioEditando('');
        setElegibleEditando(false);
        setPrecioEditando(0);
        setDescuentoEditando(0);
        setTiposPrecioAnalisisEdicion([]);
    };

    const handleGuardarComentario = async () => {
        if (analisisParaEditar && analisisParaEditar.index !== undefined) {
            // Recalcular descuento y total con el nuevo precio
            const precioFinal = precioEditando;
            const descuentoFinal = descuentoEditando;
            // 🔐 Calcular porcentaje manual REAL
            const porcentajeManual = precioFinal > 0
                ? (descuentoFinal / precioFinal) * 100
                : 0;
            const totalFinal = (precioFinal - descuentoFinal) * 1;
            
            const updatedAnalisis = [...analisisAgregados];
            updatedAnalisis[analisisParaEditar.index] = {
                ...updatedAnalisis[analisisParaEditar.index],
                comentarios: comentarioEditando,
                idTipoPrecio: tipoPrecioEditando,
                elegible: elegibleEditando,
                precio: precioFinal.toFixed(2),

                descuentoManual: true,
                porcentajeManual: porcentajeManual,

                descuento: descuentoFinal.toFixed(2),
                total: totalFinal.toFixed(2),
                
            };
            
            // Actualizar state local y recalcular totales
            setAnalisisAgregados(updatedAnalisis);
            calcularTotales(updatedAnalisis);
            
            // Recalcular elegibles si hay póliza
            if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
                recalcularElegiblesNoElegibles(updatedAnalisis);
            }
            
            // Solo ejecutar el PUT si estamos en modo edición y tenemos idDetalle
            if (IsEditing && analisisParaEditar.idDetalle) {
                try {
                    // Mostrar indicador de carga
                    Swal.fire({
                        title: 'Guardando...',
                        text: 'Actualizando detalle del análisis',
                        allowOutsideClick: false,
                        didOpen: () => {
                            Swal.showLoading();
                        }
                    });
                    
                    // Obtenemos los detalles actuales del backend para no perder datos
                    const detalleActual = await fetch(`${server}/MgOrdenesMedicasDetalles/${analisisParaEditar.idDetalle}`).then(res => res.json());
                    
                    const updateData = {
                        // Mantenemos los datos originales
                        ...detalleActual,
                        // Sobreescribimos los campos editados
                        idDetalle: analisisParaEditar.idDetalle,
                        comentarios: comentarioEditando || "",
                        idTipoPrecio: tipoPrecioEditando,
                        polizaElegible: elegibleEditando,
                        valorVenta: precioFinal,
                        valorDescuento: descuentoFinal,
                        modificadoPor: user ? user.codigoUsuario : 'ADMIN',
                    };
                    
                    // Ejecutar el PUT
                    const response = await fetch(`${server}/MgOrdenesMedicasDetalles/${analisisParaEditar.idDetalle}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(updateData)
                    });
                    
                    if (response.ok) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Detalle actualizado',
                            text: 'Los cambios se han guardado exitosamente.',
                            timer: 2000,
                            showConfirmButton: false
                        });
                    } else {
                        throw new Error('Error al actualizar el detalle');
                    }
                } catch (error) {
                    console.error('Error al actualizar el detalle:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'No se pudo actualizar el detalle. Por favor, inténtelo de nuevo.'
                    });
                }
            }
            
            handleCerrarModalEditarComentarios();
        }
    };

    const limpiarFormulario = () => {
        setClienteSeleccionado(null);
        setInputValue('');
        setIdentidad('');
        setEdad('');
        setSexo('');
        setDireccion('');
        setTelefono('');
        setCorreoElectronico('');
        setFechaCotizacion('');
        setValidez(1);
        setMedidaT('');
        setTipoPago('');
        setWhatsapp(false);
        setApp(false);
        setCorreo(false);
        setImpresos(false);
        setMedicoSeleccionado(null);
        setOrigen(null);
        // Establecer EFECTIVO (id=0) como valor por defecto al limpiar
        console.log('Limpiando formulario - buscando EFECTIVO...');
        const efectivo = mediosPago.find(m => m.id === 0);
        console.log('EFECTIVO encontrado en limpiar:', efectivo);
        setMedioPago(efectivo || null);
        setObservaciones('');
        setPerfil(null);
        setAnalisisSeleccionado(null);
        setAnalisisAgregados([]);
        setCantidad(1);
        setValor(0);
        setDescuento('');
        setTotalPagar('');
        setDescuentoTotal('');
        setDescuentoTotalP('');
        setCantidadTotal('');
        setDescuentoGlobal(0);
        setPoliza('');
        setEmpresa('');
        setNTransaccion('');
        setMinimo(0);
        setMaximo(0);
        setNVerificacion('');
        setCopago('');
        setAutorizado(false);
        setDeducible(0);
        setValorElegible(0);
        setNoElegibles(0);
        setAseguradora(0);
        setValorCopago(0);
        setTotalAsegurado(0);
        setIsAseguradora(false);
        setFechaEntrega('');

        setTipoTiempoSeleccionado(null);
        setTiempo('');
    };

    const obtenerDatosPolizaGuardada = async (idOrdenMedica) => {
    try {
        const response = await fetch(`${server}/MgOrdenesMedicasDetallePolizas/porOrden/${idOrdenMedica}`);
        if (response.ok) {
            const data = await response.json();
            const polizaData = data.$values?.[0] || data; // Tomar el primer resultado
            
            if (polizaData) {
                setIdDetallePoliza(polizaData.id || 0);
                setCopago(polizaData.porcentajeCopago || 0);
                setMinimo(polizaData.valorMinimo || 0);
                setMaximo(polizaData.valorMaximo || 0);
                setDeducible(polizaData.deducible || 0);
                setDescuentoAsegurado(polizaData.descuentoAsegurado || 0);
                setValorElegible(polizaData.elegible || 0);
                setNoElegibles(polizaData.noElegible || 0);
                setAseguradora(polizaData.aseguradora || 0);
                setValorCopago(polizaData.copago || 0);
                setTotalAsegurado(polizaData.asegurado || 0);
                
                console.log('Detalle poliza cargados:', polizaData);
            }
        }
    } catch (error) {
        console.error('Error al obtener datos de póliza guardada:', error);
    }
};

    const handleGuardarOrdenTrabajo = async () => {
        try {
            console.log('=== INICIO GUARDAR ORDEN ===');
            console.log('Modo:', modo);
            console.log('Comentarios de entrega actuales:', comentariosEntrega);
            console.log('Total comentarios:', comentariosEntrega?.length || 0);
            
            // Validaciones de campos requeridos
            const camposRequeridos = [];
            
            if (!clienteSeleccionado) {
                camposRequeridos.push('Cliente');
            }
            
            if (!medicoSeleccionado) {
                camposRequeridos.push('Médico/Doctor');
            }
            
            if (!origen) {
                camposRequeridos.push('Origen');
            }
            
            if (!medioPago) {
                camposRequeridos.push('Método de Pago');
            }

            if (camposRequeridos.length > 0) {
                const mensaje = camposRequeridos.length === 1 
                    ? `Por favor, selecciona ${camposRequeridos[0]}.`
                    : `Por favor, completa los siguientes campos requeridos: ${camposRequeridos.join(', ')}.`;
                
                swalWithHighZIndex.fire({
                    title: 'Campos requeridos',
                    text: mensaje,
                    icon: 'warning',
                    zIndex: 2000
                });
                return;
            }

            if (analisisAgregados.length === 0) {
                swalWithHighZIndex.fire({
                    title: 'Error',
                    text: 'Por favor, agrega al menos un análisis a la cotización.',
                    icon: 'error',
                    zIndex: 2000
                });
                return;
            }

            if (!whatsapp && !app && !correo && !impresos) {
                swalWithHighZIndex.fire({
                    title: 'Error',
                    text: 'Debes seleccionar al menos una opción para enviar los resultados (WhatsApp, App, Correo o Impresos).',
                    icon: 'error',
                    zIndex: 2000
                });
                return;
            }

            if (!fechaEntrega || fechaEntrega.trim() === '') {
                swalWithHighZIndex.fire({
                    title: 'Error',
                    text: 'Por favor, ingresa una fecha de entrega válida.',
                    icon: 'error',
                    zIndex: 2000
                });
                return;
            }

            // Validación para aseguradora: el monto elegible no debe superar el máximo
            if (isAseguradora && poliza && parseFloat(maximo) > 0 && parseFloat(valorElegible) > parseFloat(maximo)) {
                swalWithHighZIndex.fire({
                    title: 'Error',
                    text: `El monto elegible (${valorElegible}) supera el monto máximo de la póliza (${maximo}).`,
                    icon: 'error',
                    zIndex: 2000
                });
                return;
            }

            setIsSaving(true);

            // Mostrar indicador de progreso para órdenes grandes
            let progressDialog = null;
            if (analisisAgregados.length > 10) {
                progressDialog = Swal.fire({
                    title: 'Guardando orden de trabajo',
                    html: `Procesando ${analisisAgregados.length} análisis...`,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });
            }

            // Preparar datos de la orden de trabajo (igual que antes)
            let idOrdenMedicaMes = ordenTrabajo?.idOrdenMedicaMes;
            console.log(modo);

            if (typeof modo === 'undefined' || modo !== 'modificar') {
                const responseCant = await fetch(`${server}/MgOrdenesMedicas/cantidad`);
                const data = await responseCant.json();
                const correlativoActual = parseInt(data.toString().slice(4));
                const nuevoCorrelativo = correlativoActual + 1;

                const fechaActual = new Date();
                const year = fechaActual.getFullYear().toString().slice(-2);
                const month = (fechaActual.getMonth() + 1).toString().padStart(2, '0');

                idOrdenMedicaMes = `${year}${month}${nuevoCorrelativo}`;
            }
            const fechaOrdenMedicaValida = fechaCotizacion || new Date().toISOString().split('T')[0];


            // Preparar los datos de la orden de trabajo (mismo código)
            const OrdenTrabajoData = {
                idOrdenMedica: ordenTrabajo ? ordenTrabajo.idOrdenMedica : 0,
                idOrdenMedicaMes: idOrdenMedicaMes,
                idCliente: clienteSeleccionado.idCliente,
                correo: clienteSeleccionado.correoElectronico ? clienteSeleccionado.correoElectronico : '',
                idBarrioColonia: clienteSeleccionado.idBarrioColonia,
                // fechaOrdenMedica: fechaOrdenMedicaValida,
                fechaOrdenMedica: modo === 'modificar' ? (ordenTrabajo?.fechaOrdenMedica ?? fechaOrdenMedicaValida) : fechaOrdenMedicaValida,
                duracion: 1,
                idTipoDuracion: 1,
                idTipoPago: tipoPago ? tipoPago.id : 1,
                // valorVenta: totalPagar,
                valorVenta: valorVenta,
                valorDescuento: descuentoTotal,
                valorRecargo: deducible ? deducible : 0,
                telefono: clienteSeleccionado.telefono ? clienteSeleccionado.telefono : "",
                celular: clienteSeleccionado.celular ? clienteSeleccionado.celular : '',
                idSucursal: modo === 'modificar' ? (ordenTrabajo?.idSucursal ?? sucursalSeleccionada.idSucursal) : sucursalSeleccionada.idSucursal,
                idMedico: medicoSeleccionado ? medicoSeleccionado.idMedico : 0,
                idOrigen: origen ? origen.idOrigen : 0,
                idMedioPago: medioPago ? medioPago.id : null,
                enviarXWhatsapp: whatsapp,
                enviarXCorreo: correo,
                enviarXApp: app,
                imprimir: impresos,
                porcetajeCopago: copago,
                idPoliza,
                polizaValorMinimo: minimo ? minimo : 0,
                polizaValorMaximo: maximo ? maximo : 0,
                polizaPrevalidado: autorizado,
                idSucursalEntrega: sucursalEntrega,
                polizaNumeroTransaccion: nTransaccion,
                polizaNumeroVericacion: nVerificacion,
                polizaDeduccible: deducible,
                elegible: valorElegible,
                noElegible: noElegibles,
                descuento: descuentoTotal,
                copago: valorCopago,
                aseguradora,
                asegurado: valorCopago,
                ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                idPoliza: 0,
                idTipo: 1,
                idTipoCliente: origen ? (origen.empresa === 1 ? 2 : 1) : 1,
                ...(modo === 'modificar'
                    ? {
                        modificadoPor: user ? user.codigoUsuario : 'ADMIN',
                        ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
                    }
                    : { ingresadoPor: user ? user.codigoUsuario : 'ADMIN' }
                ),
                fechaRecepcion: fechaRecepcion,
                fechaEntrega: fechaEntrega,
                observaciones,
                analisis: analisisAgregados
            };

            console.log('Orden de trabajo data:', OrdenTrabajoData);

            const datosOriginales = ordenTrabajo ? { ...ordenTrabajo, analisis: ordenTrabajo.analisis || [] } : {};
            const datosActuales = {
                idCliente: clienteSeleccionado.idCliente,
                fechaCotizacion,
                idTipoPago: tipoPago,
                // valorVenta: totalPagar,
                valorVenta: valorVenta,
                valorDescuento: descuentoTotal,
                valorRecargo: deducible || 0,
                telefono: clienteSeleccionado.telefono || '',
                celular: clienteSeleccionado.celular || '',
                idSucursal: modo === 'modificar' ? (ordenTrabajo?.idSucursal ?? sucursalSeleccionada.idSucursal) : sucursalSeleccionada.idSucursal,
                idMedico: medicoSeleccionado ? medicoSeleccionado.idMedico : 0,
                idOrigen: origen ? origen.idOrigen : 0,
                idMedioPago: medioPago ? medioPago.id : null,
                enviarXWhatsapp: whatsapp,
                enviarXCorreo: correo,
                enviarXApp: app,
                imprimir: impresos,
                porcetajeCopago: copago,
                idPoliza,
                polizaValorMinimo: minimo || 0,
                polizaValorMaximo: maximo || 0,
                polizaPrevalidado: autorizado,
                idSucursalEntrega: sucursalEntrega,
                polizaNumeroTransaccion: nTransaccion,
                polizaNumeroVericacion: nVerificacion,
                polizaDeduccible: deducible,
                elegible: valorElegible,
                noElegible: noElegibles,
                descuento: descuentoTotal,
                copago: valorCopago,
                aseguradora,
                asegurado: valorCopago,
                ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                idPoliza: 0,
                idTipo: 1,
                idTipoCliente: origen ? (origen.empresa === 1 ? 2 : 1) : 1,
                fechaRecepcion,
                fechaEntrega,
                observaciones
            };

            // Verificar si hay cambios en los datos principales o en los comentarios
            const datosModificados = JSON.stringify(datosOriginales) !== JSON.stringify(datosActuales);
            const comentariosOriginalIds = new Set((ordenTrabajo?.comentariosEntrega || []).map(c => c.id));
            const comentariosActualIds = new Set(comentariosEntrega.filter(c => c.id).map(c => c.id));
            const comentariosNuevos = comentariosEntrega.filter(c => !c.id || c.isNew);
            const comentariosModificados = comentariosOriginalIds.size !== comentariosActualIds.size || 
                                         comentariosNuevos.length > 0 ||
                                         !Array.from(comentariosOriginalIds).every(id => comentariosActualIds.has(id));

            if (!datosModificados && !comentariosModificados) {
                swalWithHighZIndex.fire({
                    icon: 'info',
                    title: 'Sin cambios',
                    text: 'No hay cambios para guardar.'
                });
                setIsSaving(false);
                return;
            }

            const method = modo === 'modificar' ? 'PUT' : 'POST';
            if (method === 'POST') delete OrdenTrabajoData.idOrdenMedica;

            const url = modo === 'modificar'
                ? `${server}/MgOrdenesMedicas/${ordenTrabajo.idOrdenMedica}`
                : `${server}/MgOrdenesMedicas`;
            console.log('OrdenTrabajoData:', JSON.stringify(OrdenTrabajoData));
            console.log('URL:', url);
            
            // 1. PRIMERO: Guardar la orden principal
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(OrdenTrabajoData),
            });

            console.error('Response:', response);

            if (!response.ok) {
                throw new Error('Error al guardar la orden de trabajo');
            }

            const responseText = await response.text();
            let result;
            if (responseText) {
                result = JSON.parse(responseText);
            } else {
                result = {};
            }

            const idOrdenMedica = result.idOrdenMedica || (ordenTrabajo && ordenTrabajo.idOrdenMedica);

            if (!idOrdenMedica) {
                throw new Error('No se recibió el id de la orden de trabajo');
            }

            console.log('Orden guardada con ID:', idOrdenMedica);
            console.log('idOrdenMedicaMes utilizado:', idOrdenMedicaMes);

            if (!ordenTrabajo?.idOrdenMedica && ordenTrabajo?.idCotizacion) {
                console.log('Asociando orden con cotización:', ordenTrabajo.idCotizacion);

             // 1. Obtener la cotización completa
                const responseCot = await fetch(`${server}/Cotizaciones/${ordenTrabajo.idCotizacion}`);
                if (!responseCot.ok) throw new Error('No se pudo obtener la cotización');
                const cotizacionCompleta = await responseCot.json();

                // 2. Asignar el idOrden
                cotizacionCompleta.idOrdenMedica = idOrdenMedica; // o IdOrden según tu modelo
                cotizacionCompleta.modificadoPor = user ? user.codigoUsuario : 'ADMIN';
                // cotizacionCompleta.fechaModificacion = new Date().toISOString();


                // 3. Hacer el PUT con el objeto completo
               const resp = await fetch(`${server}/Cotizaciones/${ordenTrabajo.idCotizacion}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cotizacionCompleta)
        });

        if (!resp.ok) {
            console.error('Error asociando orden con cotización');
        } else {
            console.log('Orden asociada correctamente con la cotización');
        }


                }

            // 2. SEGUNDO: Manejar comentarios de entrega de forma inteligente
            
            if (modo === 'modificar') {
                try {
                    // Obtener comentarios existentes
                    const responseComentarios = await fetch(`${server}/MgOrdenesMedicasComentariosEntregas/porOrden/${idOrdenMedica}`);
                    if (responseComentarios.ok) {
                        const existingComentarios = await responseComentarios.json();
                        const existingList = existingComentarios.$values || [];
                        
                        console.log('Comentarios existentes:', existingList);
                        console.log('Comentarios actuales:', comentariosEntrega);
                        
                        // Crear mapas para comparación eficiente
                        const existingMap = new Map(existingList.map(c => [c.comentarios, c.id]));
                        const currentTexts = new Set(comentariosEntrega.map(c => c.comentarios).filter(Boolean));
                        
                        // Identificar comentarios a eliminar (existen en BD pero no en la lista actual)
                        const toDelete = existingList.filter(existing => !currentTexts.has(existing.comentarios));
                        
                        // Identificar comentarios a agregar (están en la lista actual pero no en BD)
                        const toAdd = comentariosEntrega.filter(current => 
                            validarComentario(current) && 
                            !existingMap.has(current.comentarios.trim())
                        );
                        
                        console.log('Comentarios a eliminar:', toDelete);
                        console.log('Comentarios a agregar:', toAdd);
                        
                        // Eliminar comentarios que ya no están en la lista
                        if (toDelete.length > 0) {
                                                  const username = '' + user.codigoUsuario + '/';

                            for (const comentario of toDelete) {
                                try {
                                    const deleteResponse = await fetch(
                                        `${server}/MgOrdenesMedicasComentariosEntregas/${comentario.id}/${username}`, 
                                        { method: 'DELETE' }
                                    );
                                    
                                    if (!deleteResponse.ok) {
                                        console.error(`Error eliminando comentario ${comentario.id}:`, deleteResponse.status, await deleteResponse.text());
                                    } else {
                                        console.log(`Comentario ${comentario.id} eliminado exitosamente`);
                                    }
                                } catch (deleteError) {
                                    console.error(`Error eliminando comentario ${comentario.id}:`, deleteError);
                                }
                            }
                            
                            // Esperar para que se procesen las eliminaciones
                            await new Promise(resolve => setTimeout(resolve, 200));
                        }
                        
                        // Agregar comentarios nuevos
                        if (toAdd.length > 0) {
                            for (const comentario of toAdd) {
                                try {
                                    const payloadComentario = {
                                        idOrdenMedica: idOrdenMedica,
                                        comentarios: comentario.comentarios.trim(),
                                        ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
                                    };
                                    
                                    console.log('Guardando comentario nuevo:', payloadComentario);
                                    
                                    const saveResponse = await fetch(`${server}/MgOrdenesMedicasComentariosEntregas`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(payloadComentario)
                                    });
                                    
                                    if (!saveResponse.ok) {
                                        console.error('Error guardando comentario nuevo:', saveResponse.status, await saveResponse.text());
                                    } else {
                                        console.log('Comentario nuevo guardado exitosamente');
                                    }
                                } catch (saveError) {
                                    console.error('Error guardando comentario nuevo:', saveError);
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error al manejar comentarios de entrega:', error);
                }
            } else {
                // Para órdenes nuevas, guardar todos los comentarios DESPUÉS de que se haya creado la orden
                console.log('=== PROCESANDO COMENTARIOS PARA ORDEN NUEVA ===');
                console.log('ID de orden nueva:', idOrdenMedica);
                console.log('Comentarios disponibles:', comentariosEntrega);
                
                if (comentariosEntrega && comentariosEntrega.length > 0) {
                    console.log('Guardando comentarios para orden nueva:', comentariosEntrega);
                    
                    const comentariosValidos = comentariosEntrega.filter(validarComentario);
                    
                    if (comentariosValidos.length > 0) {
                        console.log(`Guardando ${comentariosValidos.length} comentarios válidos para la orden nueva ID: ${idOrdenMedica}`);
                        
                        for (const comentario of comentariosValidos) {
                            try {
                                const payloadComentario = {
                                    idOrdenMedica: idOrdenMedica,
                                    comentarios: comentario.comentarios.trim(),
                                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
                                };
                                
                                console.log('Payload comentario nuevo:', payloadComentario);
                                
                                const saveResponse = await fetch(`${server}/MgOrdenesMedicasComentariosEntregas`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(payloadComentario)
                                });
                                
                                if (!saveResponse.ok) {
                                    const errorText = await saveResponse.text();
                                    console.error('Error guardando comentario:', saveResponse.status, errorText);
                                } else {
                                    console.log('Comentario guardado exitosamente en orden nueva');
                                }
                            } catch (saveError) {
                                console.error('Error guardando comentario:', saveError);
                            }
                        }
                    } else {
                        console.log('No hay comentarios válidos para guardar en orden nueva');
                    }
                } else {
                    console.log('No hay comentarios para guardar en orden nueva');
                }
            }

            // Actualizar los comentarios en el estado local después de guardar (tanto para nuevas como modificadas)
            try {
                await fetchComentarios(idOrdenMedica);
                console.log('Comentarios actualizados en el estado después de guardar');
            } catch (error) {
                console.error('Error actualizando comentarios después de guardar:', error);
            }

            // 3. TERCERO: Guardar datos de póliza si aplica
            if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizasOrigen)) {
                const analisisDataPoliza = {
                    idOrdenMedica: idOrdenMedica,
                    idClasificacionPorUso: 2,
                    PorcentajeCopago: parseFloat(copago) || 0,
                    valorMinimo: parseFloat(minimo) || 0,
                    valorMaximo: parseFloat(maximo) || 0,
                    deducible: parseFloat(deducible) || 0,
                    elegible: parseFloat(valorElegible) || 0,
                    noElegible: parseFloat(noElegibles) || 0,
                    DescuentoAsegurado: parseFloat(descuentoAsegurado) || 0,
                    descuento: descuentoTotal,
                    copago: valorCopago,
                    aseguradora,
                    asegurado: valorCopago,
                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                    ...(modo === 'modificar' && { 
                        modificadoPor: user ? user.codigoUsuario : 'ADMIN',
                        Id: idDetallePoliza || 0 // Si es modificación, incluir el ID del detalle de póliza 
                    })
                };
                    console.log(analisisDataPoliza);
                    const polizaMethod = modo === 'modificar' ? 'PUT' : 'POST';
                    const polizaUrl = modo === 'modificar' 
                        ? `${server}/MgOrdenesMedicasDetallePolizas/${idDetallePoliza}` 
                        : `${server}/MgOrdenesMedicasDetallePolizas`;
                            const response = await fetch(polizaUrl, {
                        method: polizaMethod,
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(analisisDataPoliza)
                    });
                    console.log('Poliza response:', response);
                    console.error('Poliza edit:', JSON.stringify(analisisDataPoliza));
            }

            // 4. OPTIMIZACIÓN: Preparar todos los datos de análisis para procesamiento en batch
            const username = '' + user.codigoUsuario + '/';
            console.log(modo);
            // En modificación: manejo optimizado de añadir/eliminar análisis
            if (modo === 'modificar') {
                const eliminados = analisisOriginal.filter(orig =>
                    !analisisAgregados.some(nuevo => nuevo.idTipoAnalisis === orig.idTipoAnalisis)
                );

                    // Para pocos elementos, usar el endpoint existente
                    const deletePromises = eliminados.map(eliminado =>
                        fetch(`${server}/MgOrdenesMedicasDetalles/${eliminado.idDetalle}/${username}`, {
                            method: 'DELETE'
                        })
                    );
                    await Promise.all(deletePromises);
                // }
            }

            // 5. OPTIMIZACIÓN PRINCIPAL: Procesar análisis en lotes para evitar sobrecarga
            const nuevos = modo === 'modificar'
                ? analisisAgregados.filter(nuevo =>
                    !analisisOriginal.some(orig => orig.idTipoAnalisis === nuevo.idTipoAnalisis)
                )
                : analisisAgregados;

            // Prefetch de muestras por análisis para evitar llamadas repetitivas
            const analisisIds = nuevos.map(a => a.idTipoAnalisis);
            const uniqueAnalisisIds = [...new Set(analisisIds)];

            // OPTIMIZACIÓN: Prefetch de muestras
            const muestrasMap = {};
            if (uniqueAnalisisIds.length > 0) {
                try {
                    // Mejor un endpoint que acepte múltiples IDs (esto debe implementarse en el backend)
                    const muestrasResponse = await fetch(`${server}/CdmMuestrasXAnalisis/multiple`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ids: uniqueAnalisisIds })
                    });

                    if (muestrasResponse.ok) {
                        const muestrasData = await muestrasResponse.json();
                        // Supongamos que devuelve {idAnalisis: [muestras]}
                        Object.assign(muestrasMap, muestrasData);
                    }
                } catch (error) {
                    console.error("Error al prefetch de muestras:", error);
                    // Continuamos con el procesamiento normal
                }
            }

            // Procesar en lotes de 10 análisis a la vez para evitar sobrecarga
            const BATCH_SIZE = 10;
            for (let i = 0; i < nuevos.length; i += BATCH_SIZE) {
                const batch = nuevos.slice(i, i + BATCH_SIZE);

                // Actualizar progreso
                if (progressDialog) {
                    Swal.update({
                        html: `Procesando análisis ${i + 1} a ${Math.min(i + BATCH_SIZE, nuevos.length)} de ${nuevos.length}...`
                    });
                }
            const fechaOrdenMedicaValida = fechaCotizacion || new Date().toISOString().split('T')[0];

                const batchPromises = batch.map(async analisis => {
                    // 1. Añadir análisis
                    const analisisData = {
                        idDetalle: 0,
                        idOrdenMedica: idOrdenMedica,
                        modificacion: 1,
                        idModulo: analisis.idModulo,
                        idTipoAnalisis: analisis.idTipoAnalisis,
                        comentarios: analisis.comentarios,
                        fechaOrdenMedica: fechaOrdenMedicaValida,
                        cantidad: analisis.cantidad,
                        idTipoPrecio: analisis.idTipoPrecio,
                        valorVenta: analisis.precio,
                        valorDescuento: analisis.descuento,
                        PolizaElegible: analisis.elegible,
                        ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                        idPromocion: analisis.idPromocion,
                       // idTipoAprobacion: 1,
                    };

                    const analisisResponse = await fetch(`${server}/MgOrdenesMedicasDetalles`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(analisisData),
                    });

                    if (!analisisResponse.ok) {
                        throw new Error(`Error al guardar el análisis ${analisis.analisis}`);
                    }

                    // 2. Procesar muestras y condiciones para este análisis
                    const muestras = muestrasMap[analisis.idTipoAnalisis] || [];
                    if (!muestras.length) {
                        // Si no están en caché, obtenerlas individualmente
                        try {
                            const muestrasResponse = await fetch(`${server}/CdmMuestrasXAnalisis/porAnalisis/${analisis.idTipoAnalisis}`);
                            if (muestrasResponse.ok) {
                                const muestrasData = await muestrasResponse.json();
                                muestras.push(...(muestrasData.$values || muestrasData));
                            }
                        } catch (error) {
                            console.error(`Error al obtener muestras para análisis ${analisis.idTipoAnalisis}:`, error);
                        }
                    }

                    // Procesar las muestras con sus condiciones
                    return Promise.all(muestras.map(async muestra => {
                        // Agregar la muestra
                        const muestraOrdenData = {
                            id_orden: idOrdenMedica,
                            id_muestra_x_analisis: muestra.idMuestraXAnalisis,
                            id_estado: 1,
                            observaciones: muestra.Observaciones || '',
                            inicio_toma_muestra: null,
                            fin_toma_muestra: null,
                            id_sucursal: sucursalSeleccionada.idSucursal,
                            id_tipo_analisis: analisis.idTipoAnalisis,
                            id_modulo: analisis.idModulo,
                            ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                        };

                        await fetch(`${server}/mg_muestras_x_ordens`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(muestraOrdenData),
                        });

                        // Obtener las condiciones solo si se insertó la muestra con éxito
                        try {
                            const condicionesResponse = await fetch(
                                `${server}/CdmCondicionesEspecialesXMuestraXAnalisis/pormuestraxanalisis/${muestra.idMuestraXAnalisis}`
                            );

                            if (!condicionesResponse.ok) {
                                return;
                            }

                            const condicionesData = await condicionesResponse.json();
                            const condiciones = condicionesData.$values || condicionesData;

                            // Insertar las condiciones
                            const condicionesPromises = condiciones.map(condicion => {
                                const condicionOrdenData = {
                                    idOrden: idOrdenMedica,
                                    idMuestraXAnalisis: muestra.idMuestraXAnalisis,
                                    idCondicion: condicion.idCondicion,
                                    cumple: false,
                                    IdTipoAnalisis: analisis.idTipoAnalisis,
                                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                                    Observaciones: condicion.Observaciones || '',
                                };

                                return fetch(`${server}/mg_condiciones_x_ordens`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(condicionOrdenData),
                                });
                            });

                            await Promise.all(condicionesPromises);
                        } catch (error) {
                            console.error(`Error procesando condiciones para muestra ${muestra.idMuestraXAnalisis}:`, error);
                        }
                    }));
                });

                await Promise.all(batchPromises);
            }

            // Cerrar el diálogo de progreso si está abierto
            if (progressDialog) {
                Swal.close();
            }            // Finalizar el proceso según el modo
            if (ordenTrabajo) {
                if (modo === 'modificar' || modo === 'dividir') {
                    swalWithHighZIndex.fire({
                        icon: 'success',
                        title: modo === 'modificar' ? 'Orden actualizada exitosamente' : 'Orden creada exitosamente',
                        showConfirmButton: false,
                        timer: 1500,
                    });

                    // Mostrar vista previa del PDF después de guardar
                    // En modo edición, no cerrar automáticamente para que el usuario pueda ver la vista previa
                    setPreviewOrdenId(idOrdenMedica); // Guardar el idOrdenMedica para el endpoint de etiquetas
                    setPreviewOrdenMedicaMes(idOrdenMedicaMes); // Guardar el idOrdenMedicaMes generado localmente
                    await handleReimprimirPreview(idOrdenMedica);

                    // Cerrar el modal y pasar datos para facturación directa
                    if (typeof onClose === 'function') {
                        onClose({
                            shouldOpenFactura: true,
                            ordenData: {
                                idOrdenMedica: idOrdenMedica,
                                nombreCompleto: clienteSeleccionado.nombreCompleto,
                                Identidad: clienteSeleccionado.numeroIdentidad,
                                valorVenta: totalPagar,
                                valorDescuento: descuentoTotal,
                                idSucursal: sucursalSeleccionada.idSucursal,
                                idCliente: clienteSeleccionado.idCliente
                            }
                        });
                    }
                } else {

                    // if (!cajaAbierta) {
                    //     swalWithHighZIndex.fire({
                    //         title: 'Error',
                    //         text: 'No hay caja abierta. Por favor, inicie la caja antes de facturar.',
                    //         icon: 'error',
                    //         zIndex: 2000
                    //     });
                    //     return;
                    // }

                    setDatosFactura({
                        ordenTrabajo: idOrdenMedica,
                        nombreCliente: clienteSeleccionado.nombreCompleto,
                        identidad: clienteSeleccionado.numeroIdentidad,
                        valorVenta: parseFloat(valorVenta) || 0,
                        valorDescuento: descuentoTotal,
                        idSucursal: sucursalSeleccionada.idSucursal,
                        idFormaPago: tipoPago,
                        idCliente: clienteSeleccionado.idCliente,
                        idOrigen: origen.idOrigen
                    });

                    limpiarFormulario();
                    setPreviewOrdenId(idOrdenMedica); // Establecer idOrdenMedica para etiquetas
                    setPreviewOrdenMedicaMes(idOrdenMedicaMes); // Establecer idOrdenMedicaMes para obtener muestras
                    await handleReimprimirPreview(idOrdenMedica);

                }
            } else {
                // Llamar a la función de reimpresión después de guardar exitosamente
                setPreviewOrdenId(idOrdenMedica); // Establecer idOrdenMedica para etiquetas
                setPreviewOrdenMedicaMes(idOrdenMedicaMes); // Establecer idOrdenMedicaMes para obtener muestras
                await handleReimprimirPreview(idOrdenMedica);

                setOrdenPreview({
                    idOrdenMedicaMes: idOrdenMedicaMes,
                });

                setMostrarPreviewOrden(true);

                // if (!cajaAbierta) {
                //     // swalWithHighZIndex.fire({
                //     //     title: 'Error',
                //     //     text: 'No hay caja abierta. Por favor, inicie la caja antes de facturar.',
                //     //     icon: 'error',
                //     //     zIndex: 2000
                //     // });
                //     limpiarFormulario();
                //     return;
                // } else {
                    setContinuarDespuesDeModal(true);
                    // Recalcular totales basándose en analisisAgregados para asegurar valores correctos
                    let valorVentaCalc = 0;
                    let descuentoTotalCalc = 0;
                    analisisAgregados.forEach((analisis) => {
                        valorVentaCalc += parseFloat(analisis.precio) * parseInt(analisis.cantidad);
                        descuentoTotalCalc += parseFloat(analisis.descuento) * parseInt(analisis.cantidad);
                    });

                    console.log('💰 Valores para GestionFactura (recalculados):', {
                        valorVentaCalc: valorVentaCalc.toFixed(2),
                        descuentoTotalCalc: descuentoTotalCalc.toFixed(2),
                        analisisAgregados: analisisAgregados.length
                    });

                    setDatosFactura({
                        ordenTrabajo: idOrdenMedica,
                        nombreCliente: clienteSeleccionado.nombreCompleto,
                        identidad: clienteSeleccionado.numeroIdentidad,
                        valorVenta: valorVentaCalc,
                        valorDescuento: descuentoTotalCalc,
                        idSucursal: sucursalSeleccionada.idSucursal,
                        idFormaPago: tipoPago,
                        idCliente: clienteSeleccionado.idCliente
                    });
                    // limpiarFormulario();
               // }

                // limpiarFormulario();
            }
        } catch (error) {
            console.error(error);
            swalWithHighZIndex.fire({
                title: 'Error',
                text: `Hubo un error al guardar la orden de trabajo: ${error.message}`,
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCloseConfirmationDialog = () => {
        setOpenConfirmationDialog(false);
    }; 
    
    const handleConfirmDespachar = async () => {
        // Validar que la caja esté abierta antes de facturar
        if (!cajaAbierta) {
            setOpenConfirmationDialog(false);
            swalWithHighZIndex.fire({
                title: 'Error',
                text: 'No hay caja abierta. Por favor, inicie la caja antes de facturar.',
                icon: 'error',
                zIndex: 2000
            });
            return;
        }

        setOpenConfirmationDialog(false);
        
        // Si estamos en modo modificar, refetchar los detalles NUEVOS para asegurar datos correctos
        if (modo === 'modificar' && ordenTrabajo?.idOrdenMedica) {
            try {
                console.log('🔄 Refetching datos de orden para facturación en modo modificar...');
                await obtenerDetallesOrdenTrabajo(ordenTrabajo.idOrdenMedica);
                
                // ⏳ Pequeño delay para asegurar que analisisAgregados se actualiza
                setTimeout(() => {
                    // Recalcular pendientes SOLO con los nuevos detalles fetched
                    const server = process.env.REACT_APP_SERVER_URL;
                    fetch(`${server}/FtFacturas/facturasbyorden/${ordenTrabajo.idOrdenMedica}`)
                        .then(res => res.json())
                        .then(data => {
                            const facturasExistentes = data.$values || data || [];
                            console.log('📊 Facturas existentes encontradas:', facturasExistentes.length);
                            
                            // Calcular pendientes filtrando detalles ya facturados
                            let valorVentaPendiente = 0;
                            let descuentoPendiente = 0;
                            let itemsPendientes = 0;
                            
                            analisisAgregados.forEach((analisis) => {
                                // Si no tiene idFactura o idFactura=0, está pendiente
                                if (!analisis.idFactura || analisis.idFactura === 0) {
                                    valorVentaPendiente += parseFloat(analisis.precio) * parseInt(analisis.cantidad);
                                    descuentoPendiente += parseFloat(analisis.descuento) * parseInt(analisis.cantidad);
                                    itemsPendientes++;
                                }
                            });
                            
                            console.log('💳 Pendientes recalculados para facturación:', {
                                valorVentaPendiente: valorVentaPendiente.toFixed(2),
                                descuentoPendiente: descuentoPendiente.toFixed(2),
                                itemsPendientes,
                                totalAnalisis: analisisAgregados.length
                            });
                            
                            setDatosFactura({
                                ordenTrabajo: idOrdenMedica,
                                nombreCliente: clienteSeleccionado.nombreCompleto,
                                identidad: clienteSeleccionado.numeroIdentidad,
                                valorVenta: valorVentaPendiente,
                                valorDescuento: descuentoPendiente,
                                idSucursal: sucursalSeleccionada.idSucursal,
                                idFormaPago: tipoPago,
                                idCliente: clienteSeleccionado.idCliente
                            });
                            setShowGestionFactura(true);
                        })
                        .catch(err => {
                            console.error('Error al obtener facturas existentes:', err);
                            // Fallback: usar analisisAgregados directamente
                            let valorVentaPendiente = 0;
                            let descuentoPendiente = 0;
                            analisisAgregados.forEach((analisis) => {
                                if (!analisis.idFactura || analisis.idFactura === 0) {
                                    valorVentaPendiente += parseFloat(analisis.precio) * parseInt(analisis.cantidad);
                                    descuentoPendiente += parseFloat(analisis.descuento) * parseInt(analisis.cantidad);
                                }
                            });
                            setDatosFactura({
                                ordenTrabajo: idOrdenMedica,
                                nombreCliente: clienteSeleccionado.nombreCompleto,
                                identidad: clienteSeleccionado.numeroIdentidad,
                                valorVenta: valorVentaPendiente,
                                valorDescuento: descuentoPendiente,
                                idSucursal: sucursalSeleccionada.idSucursal,
                                idFormaPago: tipoPago,
                                idCliente: clienteSeleccionado.idCliente
                            });
                            setShowGestionFactura(true);
                        });
                }, 100);
            } catch (error) {
                console.error('Error refetching orden:', error);
                setShowGestionFactura(true);
            }
        } else {
            setShowGestionFactura(true);
        }
    };



    const handleClienteSeleccionado = (event, newValue) => {
        setClienteSeleccionado(newValue); // Guardar el cliente seleccionado
        // Restablecer el descuento
        console.log(newValue);
        setDescuento(0);
        if (newValue) {
            setIdentidad(newValue.numeroIdentidad); // Asignar valores adicionales
            const fechaNacimiento = new Date(newValue.fechaNacimiento); // Obtener la fecha de nacimiento
            const hoy = new Date(); // Obtener la fecha actual

            // Calcular años y meses
            let edadAnios = hoy.getFullYear() - fechaNacimiento.getFullYear();
            let edadMeses = hoy.getMonth() - fechaNacimiento.getMonth();

            if (edadMeses < 0) {
                edadAnios--; // Restar un año si aún no ha pasado el mes de cumpleaños
                edadMeses += 12; // Ajustar los meses
            }

            const edadCalculada = `${edadAnios} años y ${edadMeses} meses`;

            // Asignar datos de cliente
            setEdad(edadCalculada);
            setSexo(newValue.idSexo);
            setDireccion(newValue.direccionCompleta);
            setTelefono(newValue.celular);
            setCorreoElectronico(newValue.correoElectronico);

        } else {
            // Si no hay cliente seleccionado, vaciar los campos
            setIdentidad('');
            setEdad('');
            setSexo('');
            setDireccion('');
            setTelefono('');
            setCorreoElectronico('');
        }
    };

// Detectar si el componente está en modo modal (se abrió como ventana emergente)
const isModal = typeof onClose === 'function';

// Estilo optimizado del modal (márgenes laterales reducidos similar a TomaMuestra)
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // width: isModal ? '96%' : '85%',
    width: '96%',
    // maxWidth: '1700px',
    height: '92vh',
    backgroundColor: '#fff',
    padding: isModal ? '8px 10px 12px 10px' : '20px',
    boxShadow: 24,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden'
};

// Espaciado reducido en modo modal
const gridSpacing = isModal ? 1 : 2;
    // Manejar tecla Escape para cerrar la vista previa en modo edición
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && openReimprimirPreview && modo === 'modificar') {
                handleCloseReimprimirPreview();
            }
        };

        if (openReimprimirPreview && modo === 'modificar') {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [openReimprimirPreview, modo]);

    const handleCloseReimprimirPreview = () => {
        if (reimprimirPdfUrl) {
            window.URL.revokeObjectURL(reimprimirPdfUrl);
            setReimprimirPdfUrl(null);
        }
        setOpenReimprimirPreview(false);
        setReimprimirError(null);

        //Limpiar formulario SOLO si no estamos en modo modificar
        if (modo !== 'modificar') {
            limpiarFormulario();
        }
        
        // Solo cerrar el modal principal si el usuario no está en proceso de facturación
        // o si está creando una nueva orden (no modificando)
        if (modo !== 'modificar' && typeof onClose === 'function') {
            onClose();
        }
    };

    const handleReimprimirPreview = async (idOrdenMedica) => {
        if (!idOrdenMedica) {
            Swal.fire({ text: 'No se pudo obtener el ID de la orden de trabajo.', icon: 'error' });
            return;
        }
        setPreviewOrdenId(idOrdenMedica); // Guardar el ID para usarlo después
        setReimprimirLoading(true);
        setReimprimirError(null);
        setOpenReimprimirPreview(true);
        try {
            const response = await fetch(`${server}/MgOrdenesMedicas/ImprimirOrdenMedica/${idOrdenMedica}`, {
                method: 'POST',
            });
            if (!response.ok) {
                throw new Error(`Error al generar el PDF: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setReimprimirPdfUrl(url);
        } catch (err) {
            setReimprimirError(err.message || 'Error al generar la vista previa del PDF');
        } finally {
            setReimprimirLoading(false);
        }
    };

    // Función para agrupar muestras por descripción, módulo, cantidad Y ESTADO (EXACTAMENTE como TomaMuestra)
    const agruparMuestrasParaEtiquetas = (muestrasData) => {
        const muestrasAgrupadas = new Map();
        
        muestrasData.forEach((muestra, index) => {
            // ✅ INCLUIR ESTADO EN LA CLAVE (igual que TomaMuestra)
            const estadoId = muestra.idEstado || 1;
            const clave = `${muestra.descripcionMuestra}-${muestra.modulo}-${muestra.descripcionCantidad}-${estadoId}`;
            
            if (muestrasAgrupadas.has(clave)) {
                // Si ya existe, agregar el análisis a la lista
                const muestraExistente = muestrasAgrupadas.get(clave);
                muestraExistente.analisis.push(muestra.ComparteCon);
                muestraExistente.examenes.push(muestra);
                
                // Combinar históricos si existen
                if (muestra.historico && muestra.historico.$values && muestra.historico.$values.length > 0) {
                    muestraExistente.historicoCompleto.push(...muestra.historico.$values);
                }
            } else {
                // Extraer histórico inicial
                let historicoInicial = [];
                if (muestra.historico) {
                    if (muestra.historico.$values && Array.isArray(muestra.historico.$values)) {
                        historicoInicial = [...muestra.historico.$values];
                    } else if (Array.isArray(muestra.historico)) {
                        historicoInicial = [...muestra.historico];
                    }
                }
                
                // Crear nueva entrada agrupada
                muestrasAgrupadas.set(clave, {
                    idMuestraXAnalisis: muestra.idMuestraXAnalisis,
                    modulo: muestra.modulo || 'N/A',
                    descripcionMuestra: muestra.descripcionMuestra,
                    descripcionCantidad: muestra.descripcionCantidad,
                    cantidad: muestra.descripcionCantidad,
                    observaciones: muestra.observaciones,
                    analisis: [muestra.ComparteCon],
                    examenes: [muestra],
                    ComparteCon: muestra.ComparteCon,
                    esCobas303: muestra.esCobas303 || false,
                    cantidadMuestras: 1,
                    idEstado: estadoId,
                    estadoDescripcion: muestra.estadoDescripcion || 'Pendiente',
                    historicoCompleto: historicoInicial
                });
            }
        });
        
        // Convertir el Map a array y formatear ComparteCon
        const resultado = Array.from(muestrasAgrupadas.values()).map(muestra => ({
            ...muestra,
            ComparteCon: muestra.analisis.join(', '),
            cantidadMuestras: muestra.examenes.length,
            // Ordenar histórico por fecha (más reciente primero)
            historicoCompleto: muestra.historicoCompleto.sort((a, b) => {
                const fechaA = new Date(a.fecha_cambio_estado || 0);
                const fechaB = new Date(b.fecha_cambio_estado || 0);
                return fechaB - fechaA;
            })
        }));
        
        return resultado;
    };

    // Funciones para manejar las etiquetas
    const handleImprimirEtiquetas = async () => {
        // ✅ IMPORTANTE: El endpoint /mg_muestras_x_ordens/pororden/{id} requiere idOrdenMedica, NO idOrdenMedicaMes
        const idOrdenMedica = previewOrdenId || (ordenTrabajo && ordenTrabajo.idOrdenMedica);
        
        console.log('🏷️ [ETIQUETAS] Debug:');
        console.log('previewOrdenId:', previewOrdenId);
        console.log('ordenTrabajo:', ordenTrabajo);
        console.log('idOrdenMedica final:', idOrdenMedica);
        
        if (idOrdenMedica) {
            console.log('📦 [MUESTRAS] Usando idOrdenMedica:', idOrdenMedica);
            try {
                // ✅ USAR LA MISMA API QUE TomaMuestra (con filtrado del backend)
                const response = await fetch(`${server}/mg_muestras_x_ordens/pororden/${idOrdenMedica}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': key,
                    },
                });
                
                if (response.ok) {
                    const muestrasData = await response.json();

                    // Detectar si es sucursal principal (ID = 2)
                    const esSucursalPrincipal = sucursalSeleccionada?.idSucursal === 2;
                    
                    // Procesar muestras desde el nuevo endpoint
                    const samples = [];
                    const muestrasArray = muestrasData.$values || muestrasData;
                    
                    muestrasArray.forEach(muestra => {
                        // Pre-seleccionar COBAS si es sucursal principal Y módulo es bioquímica clínica
                        const moduloTexto = (muestra.modulo || '').toLowerCase();
                        const esBioquimicaClinica = moduloTexto.includes('bioquimica') || 
                                                    moduloTexto.includes('bioquímica') ||
                                                    (moduloTexto.includes('bio') && moduloTexto.includes('clinica'));
                        
                        const preSeleccionarCobas = esSucursalPrincipal && esBioquimicaClinica;
                        
                        samples.push({
                            idMuestraXAnalisis: muestra.idMuestraXAnalisis,
                            modulo: muestra.modulo || 'N/A',
                            descripcionMuestra: muestra.descripcionMuestra,
                            descripcionCantidad: muestra.cantidad,
                            observaciones: muestra.observaciones,
                            ComparteCon: muestra.analisis,
                            esCobas303: preSeleccionarCobas || false,
                            idEstado: muestra.idEstado || 1,
                            estadoDescripcion: muestra.estadoDescripcion || 'Pendiente'
                        });
                    });

                    if (samples.length === 0) {
                        swalWithHighZIndex.fire({
                            title: 'Sin muestras',
                            text: 'No se encontraron muestras para esta orden.',
                            icon: 'info',
                            zIndex: 10000
                        });
                        return;
                    }

                    // Agrupar muestras igual que TomaMuestra
                    const muestrasAgrupadas = agruparMuestrasParaEtiquetas(samples);
                    
                    setAvailableSamples(muestrasAgrupadas);
                    setSelectedSamples([]);
                    setOpenLabelModal(true);
                } else {
                    const errorData = await response.json();
                    const errorMessage = errorData.message || 'Error al obtener los datos de la orden';
                    swalWithHighZIndex.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                        zIndex: 10000
                    });
                }
            } catch (error) {
                swalWithHighZIndex.fire({
                    icon: 'error',
                    title: 'Error al obtener muestras',
                    text: error.message || 'Ocurrió un error inesperado',
                    zIndex: 10000
                });
            }
        } else {
            swalWithHighZIndex.fire({
                text: 'No hay orden disponible para imprimir etiquetas.',
                icon: 'warning',
                zIndex: 10000
            });
        }
    };

    // Funciones auxiliares para el modal de etiquetas
    const handleCloseLabelModal = () => {
        setOpenLabelModal(false);
        setSelectedSamples([]);
        setAvailableSamples([]);
    };

    const handleSampleSelectionChange = (muestraAgrupada, checked) => {
        if (checked) {
            // Agregar solo el ID principal del grupo (como TomaMuestra)
            setSelectedSamples(prev => [...prev, muestraAgrupada.idMuestraXAnalisis]);
        } else {
            // Quitar el ID principal del grupo
            setSelectedSamples(prev => prev.filter(id => id !== muestraAgrupada.idMuestraXAnalisis));
        }
    };

    const handleSelectAllSamples = (checked) => {
        if (checked) {
            // Recoger solo los IDs principales de cada grupo (como TomaMuestra)
            const todosLosIds = availableSamples.map(muestra => muestra.idMuestraXAnalisis);
            setSelectedSamples(todosLosIds);
        } else {
            setSelectedSamples([]);
        }
    };

    const handlePrintSelectedLabels = async () => {
        if (selectedSamples.length === 0) {
            swalWithHighZIndex.fire({
                text: 'Debe seleccionar al menos una muestra para imprimir etiquetas',
                icon: 'warning',
                zIndex: 10000
            });
            return;
        }

        try {
            // Usar el idOrdenMedica correcto para el endpoint de etiquetas, con fallback a idOrdenMedicaMes si es necesario
            const idOrdenMedica = previewOrdenId || previewOrdenMedicaMes || (ordenTrabajo && ordenTrabajo.idOrdenMedica);
            console.log('🖨️ [IMPRIMIR] Usando idOrdenMedica:', idOrdenMedica);
            
            // Crear el array de objetos con la estructura esperada por el backend - EXACTAMENTE como TomaMuestra
            const muestrasSeleccionadas = selectedSamples.map(idMuestra => {
                const muestra = availableSamples.find(m => m.idMuestraXAnalisis === idMuestra);
                return {
                    IdMuestraXAnalisis: idMuestra,
                    EsCobas303: muestra?.esCobas303 || false
                };
            });

            // Usar idOrdenMedica en la URL como se hace en OrdenesTrabajo.jsx
            const response = await fetch(`${server}/MgOrdenesMedicas/ImprimirEtiquetasSeleccionadas/${idOrdenMedica}?anchoMM=60&altoMM=30`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'XApiKey': key
                },
                body: JSON.stringify(muestrasSeleccionadas)
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                
                // Mostrar el PDF en ventana emergente (modal) en lugar de descargar - como TomaMuestra
                setReimprimirPdfUrl(url);
                setOpenReimprimirPreview(true);
                setOpenLabelModal(false);
                
                swalWithHighZIndex.fire({
                    title: 'Éxito',
                    text: 'Las etiquetas se han generado correctamente',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    zIndex: 10000
                });
            } else {
                const errorText = await response.text();
                swalWithHighZIndex.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    text: errorText || 'Error al generar las etiquetas',
                    zIndex: 10000
                });
            }
        } catch (error) {
            console.error('Error al imprimir etiquetas:', error);
            swalWithHighZIndex.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al imprimir las etiquetas',
                zIndex: 10000
            });
        }
    };

    // Inicializar referencia a prioridadDescuento para evitar renderizado con valores indefinidos
    const prioridadDescuentoRef = typeof prioridadDescuento !== 'undefined' ? prioridadDescuento : [];
    
    return (
        <>
            {/* Gestor de descuentos - Implementa FASE 1 del plan de acción */}
            <GestorDescuentos
                origen={origen}
                medicoSeleccionado={medicoSeleccionado}
                clienteSeleccionado={clienteSeleccionado}
                medioPago={medioPago}
                fechaCotizacion={fechaCotizacion}
                analisisAgregados={analisisAgregados || []}
                setAnalisisAgregados={setAnalisisAgregados}
                setDescuentoGlobal={setDescuentoGlobal}
                setDescuentoGlobalRemitido={setDescuentoGlobalRemitido}
                setDescuentoAsegurado={setDescuentoAsegurado}
                calcularTotales={calcularTotales}
                valorCopago={valorCopago || 0}
                server={server}
                perfilSeleccionado={perfilDescuento} // Pasar el perfil seleccionado
                prioridadDescuento={prioridadDescuentoRef}
            />
            
                        <div
                            className={isModal ? '' : 'right_col'}
                            role="main"
                            style={{
                                height: '100%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                maxHeight: 'calc(90vh - 20px)',
                                margin: isModal ? 0 : undefined,
                                padding: isModal ? 0 : undefined
                            }}
                        >
                                <div className="x_title" style={isModal ? { margin: 0, padding: 0 } : {}}></div>
                                <div className={isModal ? '' : 'x_content'} style={isModal ? { padding: '4px 6px' } : {}}>
                                        <Grid container spacing={gridSpacing}>
                        {/* Header Sticky del Cliente */}
                        <Grid item xs={12} sx={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#fff',
                            zIndex: 100,
                            borderBottom: '2px solid #e0e0e0',
                            paddingBottom: '8px',
                            marginBottom: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                marginBottom: '8px'
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', margin: 0 }}>
                                    {clienteSeleccionado?.nombreCompleto || 'Sin cliente seleccionado'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Chip 
                                        label={`ID: ${identidad || 'N/A'}`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                    />
                                    <Chip 
                                        label={`${edad || 'N/A'} años`} 
                                        size="small" 
                                        color="default"
                                    />
                                    <Chip 
                                        label={`Tel: ${telefono || 'N/A'}`} 
                                        size="small" 
                                        color="default"
                                    />
                                    <Chip 
                                        label={`Origen: ${origen?.descripcion || 'N/A'}`} 
                                        size="small" 
                                        color="info"
                                        variant="outlined"
                                    />
                                    <Chip 
                                        label={`Análisis: ${analisisAgregados.length}`} 
                                        size="small" 
                                        color="secondary"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        
                        {/* Sección de Datos del Cliente */}
                        <Grid item xs={12}>
                            <h2>Datos del Cliente</h2>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                size="small"
                                freeSolo
                                fullWidth
                                options={clientes}
                                value={clienteSeleccionado}
                                getOptionLabel={(option) => option?.nombreCompleto || ''}
                                filterOptions={createSmartFilter(['nombreCompleto', 'identidad', 'telefono'])}
                                inputValue={inputValue}
                                isOptionEqualToValue={(option, value) => option?.idCliente === value?.idCliente}
                                onInputChange={handleInputChange}
                                onChange={handleClienteSeleccionado}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar cliente"
                                        variant="outlined"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();

                                                // Obtener el texto actual del input
                                                const searchText = e.target.value;

                                                // Función para manejar la selección después de la llamada a la API
                                                const handleEnterSelect = async () => {
                                                    try {
                                                        // Mostrar indicador de carga
                                                        setIsLoading(true);

                                                        // Llamar directamente a la API y esperar su respuesta
                                                        const response = await fetch(`${server}/MgClientes/buscador/${searchText}`, {
                                                            method: 'GET',
                                                            headers: { 'Content-Type': 'application/json' },
                                                        });

                                                        const data = await response.json();

                                                        // Actualizar la lista de clientes con los resultados de la API
                                                        if (data && data.clientes && Array.isArray(data.clientes.$values)) {
                                                            const clientesData = data.clientes.$values;
                                                            setClientes(clientesData);

                                                        }
                                                    } catch (error) {
                                                        console.error('Error al cargar los clientes:', error);
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                };

                                                // Ejecutar la función asíncrona
                                                handleEnterSelect();
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={1.5}>
                            <TextField size="small" label="Identidad" value={identidad || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Edad" value={edad || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Sexo</InputLabel>
                                <Select
                                    size="small"
                                    value={sexo || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={(e) => setSexo(e.target.value)}
                                    required
                                    inputProps={{ readOnly: true }}
                                >
                                    {sexos.map((sexo) => (
                                        <MenuItem key={sexo.idSexo} value={sexo.idSexo}>
                                            {sexo.descripcionSexo}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                         <Grid item xs={6} md={2}>
                            <TextField size="small" label="Celular" value={telefono || ''} fullWidth inputProps={{ readOnly: false }} />
                        </Grid>
                         <Grid item xs={12} md={1}>
                            <Button sx={{padding:'7px 6px', minWidth:'auto'}} size="small" variant="contained" color="primary" onClick={handleOpenModalCliente} disabled={!tienePermiso('Editar Cliente desde OT')}>Editar Cliente</Button>
                        </Grid>
                        <Grid item xs={12} md={5.5}>
                            <TextField size="small" label="Dirección" value={direccion || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        {/* <Grid item xs={2}>
                            <TextField size="small" label="Celular" value={telefono || ''} fullWidth inputProps={{ readOnly: false }} />
                        </Grid> */}
                        <Grid item xs={12} md={2.5}>
                            <TextField size="small" label="Correo Electronico" value={correoElectronico || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={1.5}>
                            <TextField size="small" label="Fecha" value={fechaCotizacion || ''} fullWidth type="date" InputLabelProps={{ shrink: true, readOnly: true }} onChange={(e) => setFechaCotizacion(e.target.value)} />
                        </Grid>


                        <Grid item xs={12} md={2.5}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Sucursal Entrega</InputLabel>
                                <Select
                                    size="small"
                                    value={sucursalEntrega || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={(e) => setSucursalEntrega(e.target.value)}
                                    required

                                >
                                    {sucursales.map((suc) => (
                                        <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                                            {suc.nombreSucursal}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={1.5}>
                            <TextField
                                size="small"
                                label="Fecha Hora Recepcion"
                                value={fechaRecepcion || ''}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => setFechaRecepcion(e.target.value)}
                                inputProps={{ readOnly: true }}
                            />
                        </Grid>

                        <Grid item xs={6} md={1.3}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Tipo Tiempo</InputLabel>
                                <Select
                                    size="small"
                                    value={tipoTiempoSeleccionado || ''}
                                    onChange={(e) => setTipoTiempoSeleccionado(e.target.value)}
                                    required

                                >
                                    {tipoTiempoOptions.map((tipoP) => (
                                        <MenuItem key={tipoP.value} value={tipoP.value}>
                                            {tipoP.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={1}>
                            <TextField
                                size="small"
                                label="Tiempo"
                                type="number"
                                value={tiempo}
                                onChange={(e) => setTiempo(e.target.value)}
                                fullWidth
                            />

                        </Grid>
                        <Grid item xs={12} md={1.5}>
                            <TextField
                                size="small"
                                label="Fecha Hora Entrega"
                                value={fechaEntrega || ''}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true, readOnly: true }}
                                onChange={(e) => setFechaEntrega(e.target.value)}
                            />
                        </Grid>

                        {/* <Grid container item xs={12} justifyContent="center" spacing={2} sx={{ mt: 0.4 }}> */}
                            <Grid item xs={12} md={2} sx={{marginTop:'9px'}}>
                                <h4>Resultados por:</h4>
                            </Grid>
                            <Grid item xs={6} md={1} >
                                <FormControlLabel
                                    size="small"
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={whatsapp}
                                            onChange={(e) => setWhatsapp(e.target.checked)}
                                        />
                                    }
                                    label="WhatsApp"
                                />
                            </Grid>
                            <Grid item xs={6} md={1}>
                                <FormControlLabel
                                    size="small"
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={app}
                                            onChange={(e) => setApp(e.target.checked)}
                                        />
                                    }
                                    label="App"
                                />
                            </Grid>
                            <Grid item xs={6} md={1} >
                                <FormControlLabel
                                    size="small"
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={correo}
                                            onChange={(e) => setCorreo(e.target.checked)}
                                        />
                                    }
                                    label="Correo"
                                />
                            </Grid>
                            <Grid item xs={6} md={1} >
                                <FormControlLabel
                                    size="small"
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={impresos}
                                            onChange={(e) => setImpresos(e.target.checked)}
                                        />
                                    }
                                    label="Impresos"
                                />
                            </Grid>
                        {/* </Grid> */}

                        {/* Sección de Datos de la Cotización */}
                        <Grid item xs={12} style={{padding:'0px'}}>
                            <h2>Datos de la orden de trabajo</h2>
                        </Grid>
                        <Grid item xs={12} md={4}>
                    

                            <Autocomplete
                                size="small"
                                options={Medicos}
                                value={medicoSeleccionado}
                                onChange={(e, newValue) => {
                                    setMedicoSeleccionado(newValue);
                                    console.log("Médico seleccionado:", newValue);
                                }}
                                getOptionLabel={(option) => option.nombreMedico || ''}
                                isOptionEqualToValue={(option, value) => option?.idMedico === value?.idMedico}

                                filterOptions={(options, params) => {
                                    const input = normalizeText(params.inputValue || '');

                                    // 🔹 Caso especial: mostrar SOLO N/A si el usuario escribe "n/a"
                                    if (input === 'n/a') {
                                        return options.filter(
                                            opt => normalizeText(opt.nombreMedico) === 'n/a'
                                        );
                                    }

                                    // 🔹 Aplicar createSmartFilter normalmente
                                    const filtered = createSmartFilter(['nombreMedico'], 25)(options, params);

                                    // 🔹 Excluir N/A del resto de los resultados
                                    return filtered.filter(
                                        opt => normalizeText(opt.nombreMedico) !== 'n/a'
                                    );
                                }}

                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Nombre del Médico"
                                        variant="outlined"
                                    />
                                }
                            />

                            </Grid>

                           <Grid item xs={12} md={4}>
                               <Autocomplete
                                    size="small"
                                    options={origenes}
                                    value={origen}
                                    onChange={(e, newValue) => setOrigen(newValue)}
                                    onInputChange={(e, inputValue, reason) => {
                                        // 🔹 Limpiar la selección anterior si el usuario escribe algo
                                        if (reason === "input"  && inputValue.length > lastInput.length) {
                                            setOrigen(null);
                                        }

                                        setLastInput(inputValue);
                                    }}
                                    getOptionLabel={(option) => option?.descripcion || ''}
                                    isOptionEqualToValue={(option, value) => option?.idOrigen === value?.idOrigen}
                                    filterOptions={(options, params) => {
                                        const inputOrigenes = normalizeText(params.inputValue ||"");

                                        // 🔹 Caso especial: N/A solo si el input es exactamente "n/a"
                                        if (inputOrigenes === 'n/a') {
                                            return options.filter(opt => normalizeText(opt.descripcion) === 'n/a');
                                        }

                                        // 🔹 Usar createSmartFilter normalmente, pero excluir N/A
                                        const filtered = createSmartFilter(['descripcion'], 25)(options, params);
                                        return filtered.filter(opt => normalizeText(opt.descripcion) !== 'n/a');
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Origen" variant="outlined" />}
                                    // renderOption={(props, option) => <li {...props}>{option.descripcion || ''}</li>}
                                />

                                </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Autocomplete
                                                size="small"
                                                options={mediosPago}
                                                value={medioPago}
                                                onChange={(e, newValue) => {
                                                    setMedioPago(newValue);
                                                }}
                                                getOptionLabel={(option) => option.descripcion || ''}
                                                filterOptions={createSmartFilter(['descripcion'])}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id} // Comparación por ID del medio de pago
                                                renderInput={(params) => <TextField {...params} label="Medio de Pago" variant="outlined" />}
                                            />
                                        </Grid>
                        <Grid item xs={12} md={3}>
                            {isAseguradora && (
                                <Typography variant="h5" style={{ color: 'red' }}>
                                    Aseguradora
                                </Typography>
                            )}
                        </Grid>


                        {/* Sección de Datos del Análisis */}
                        <Grid item xs={12} style={{padding:'0px'}}>
                            <h2>Datos de Análisis</h2>
                        </Grid>
                        <Grid container>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={perfiles}
                                   getOptionLabel={(option) => option && option.descripcionPerfil ? option.descripcionPerfil : ''}
                                    value={perfil}
                                    filterOptions={createSmartFilter(['descripcionPerfil'])}
                                    onChange={async (event, newValue) => {
                                        // Debugging detallado del perfil seleccionado
                                        if (newValue) {
                                            console.log('✅ Perfil seleccionado en Autocomplete:', newValue);
                                            console.log('   - ID:', newValue.idPerfil);
                                            console.log('   - Descripción:', newValue.descripcionPerfil);
                                            console.log('   - Porcentaje de descuento:', newValue.porcentaje);
                                            console.log('   - Descuento remitido:', newValue.descuento_remitido);
                                            
                                            // Verificar si el perfil tiene todos los campos necesarios
                                            if (newValue.porcentaje === undefined || newValue.porcentaje === null) {
                                                console.warn('⚠️ El perfil no tiene porcentaje de descuento definido');
                                            }
                                                    setPerfilDescuento(newValue);

                                        } else {
                                            console.log('❌ Se ha deseleccionado el perfil');
                                        }
                                        
                                        setPerfil(newValue);
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.idPerfil === value?.idPerfil} // Comparar por ID de perfil
                                    renderOption={(props, option) => {
                                        const aplicable = esPromocionAplicableAlCliente(option);
                                        const tienePromocion = option.porcentaje > 0;
                                        return (
                                        <li 
                                            {...props} 
                                            style={{
                                                ...props.style,
                                                backgroundColor: aplicable ? '#e8f5e8' : (tienePromocion ? '#fff3e0' : 'transparent'),
                                                color: aplicable ? '#2e7d32' : 'inherit',
                                                fontWeight: aplicable ? 'bold' : 'normal',
                                                borderLeft: aplicable ? '4px solid #4caf50' : (tienePromocion ? '4px solid #ff9800' : 'none'),
                                                paddingLeft: (aplicable || tienePromocion) ? '12px' : '16px'
                                            }}
                                        >
                                            {option.descripcionPerfil || ''}
                                            {aplicable && (
                                                <span style={{ 
                                                    marginLeft: '8px', 
                                                    fontSize: '0.8em', 
                                                    backgroundColor: '#4caf50', 
                                                    color: 'white', 
                                                    padding: '2px 6px', 
                                                    borderRadius: '10px' 
                                                }}>
                                                    Vigente
                                                </span>
                                            )}
                                            {tienePromocion && !aplicable && (
                                                <span style={{ 
                                                    marginLeft: '8px', 
                                                    fontSize: '0.8em', 
                                                    backgroundColor: '#ff9800', 
                                                    color: 'white', 
                                                    padding: '2px 6px', 
                                                    borderRadius: '10px' 
                                                }}>
                                                    No aplica
                                                </span>
                                            )}
                                        </li>
                                        );
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Selecciona un Perfil" variant="outlined" onKeyDown={handlePerfilKeyDown} />}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button size="small" variant="contained" color="primary" onClick={handleAgregarAnalisisPerfil} disabled={!tienePermiso('Agregar Perfil Análisis OT')}>Agregar Analisis de Perfil</Button>
                            </Grid>
                        </Grid>
                    
                        

                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                size="small"
                                options={analisis}
                                value={analisisSeleccionado}
                                inputValue={inputAnalisisValue}
                                onInputChange={(_, newInputValue) => {
                                    setInputAnalisisValue(newInputValue);
                                }}
                                getOptionLabel={(option) => option?.descripcionTipoAnalisis || ''}
                                filterOptions={createRestrictiveFilter(
                                    ['descripcionTipoAnalisis'],
                                    20,
                                    analisisAgregados
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option?.idTipoAnalisis === value?.idTipoAnalisis
                                }
                                onChange={async (_, newValue, reason) => {

                                    if (!newValue) {
                                        setAnalisisSeleccionado(null);
                                        setInputAnalisisValue('');
                                        return;
                                    }

                                    if (reason === 'selectOption') {

                                        // ✅ Evitar duplicados
                                        if (
                                            analisisAgregados.some(
                                                a => a.idTipoAnalisis === newValue.idTipoAnalisis
                                            )
                                        ) {
                                            Swal.fire({
                                                icon: 'warning',
                                                title: 'Análisis ya agregado',
                                                text: 'El análisis ya ha sido agregado. No se pueden duplicar.',
                                                timer: 1500,
                                                showConfirmButton: false,
                                                toast: true,
                                                position: 'top-end'
                                            });

                                            setAnalisisSeleccionado(null);
                                            setInputAnalisisValue('');
                                            return;
                                        }

                                        await handleSeleccionAnalisis(newValue);
                                        setAnalisisSeleccionado(newValue);
                                    }

                            
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Análisis"
                                        variant="outlined"
                                        inputRef={refAutocompleteAnalisis}
                                        onKeyDown={async (e) => {

                                            if (e.key !== 'Enter') return;
                                            e.preventDefault();

                                                // ✅ VALIDACIÓN DE CLIENTE
                                                if (!clienteSeleccionado) {
                                                    Swal.fire({
                                                        icon: 'warning',
                                                        title: 'Cliente requerido',
                                                        text: 'Debes seleccionar un cliente antes de agregar un análisis.',
                                                        timer: 1800,
                                                        showConfirmButton: false,
                                                    });
                                                    return;
                                                }

                                                // 🔹 Si aún no hay selección (primer Enter), dejar que MUI haga selectOption
                                                if (!analisisSeleccionado) return;

                                                // ✅ Evitar duplicados
                                                if (
                                                    analisisAgregados.some(
                                                        a => a.idTipoAnalisis === analisisSeleccionado.idTipoAnalisis
                                                    )
                                                ) {
                                                    Swal.fire({
                                                        icon: 'warning',
                                                        title: 'Análisis ya agregado',
                                                        text: 'El análisis ya ha sido agregado.',
                                                        timer: 1200,
                                                        showConfirmButton: false,
                                                        toast: true,
                                                        position: 'top-end'
                                                    });
                                                    return;
                                                }

                                                // ✅ Agregar
                                                await handleAgregarAnalisis(analisisSeleccionado);

                                                Swal.fire({
                                                    icon: 'success',
                                                    title: 'Análisis agregado',
                                                    text: 'Se agregó correctamente',
                                                    timer: 1200,
                                                    showConfirmButton: false,
                                                    toast: true,
                                                    position: 'top-end'
                                                });

                                                // 🔥 limpiar completamente
                                                setAnalisisSeleccionado(null);
                                                setInputAnalisisValue('');

                                        }}
                                    />
                                )}
                            />
                        </Grid>

                    
                        <Grid item xs={12} md={2}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Tipo Precio</InputLabel>
                                <Select
                                    size="small"
                                    value={tipoPrecio || ''} // Asegúrate de que 'tipoPrecio' tenga un valor válido
                                    onChange={handleTipoPrecioChange}
                                    required

                                >
                                    {tiposPrecioAnalisis.map((tipoP) => (
                                        <MenuItem key={tipoP.idTipoPrecio} value={tipoP.idTipoPrecio}>
                                            {tipoP.descripcionTipoPrecio}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Valor" type="number" value={valor} onChange={(e) => setValor(e.target.value)} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                size="small"
                                label="Desc. (%)"
                                type="number"
                                value={descuento}
                                fullWidth
                                inputProps={{ step: "0.01", readOnly: !puedeCambiarDescuento }}
                                onChange={(e) => {
                                    const nuevoValor = parseFloat(e.target.value);

                                    const valorFinal = isNaN(nuevoValor) ? 0 : nuevoValor;

                                    setDescuento(valorFinal);

                                    descuentoManualRef.current = true;
                                }}
                                InputProps={{
                                    endAdornment: !puedeCambiarDescuento && (
                                        <IconButton onClick={() => setOpenModalDescuento(true)}>
                                            <LockIcon />
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={1}>
                            <TextField
                                size="small"
                                label="Cantidad"
                                type="number"
                                value={cantidad}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    // si mete algo >1, forzamos a 1
                                    if (val > 1) {
                                        setCantidad(1);
                                    } else if (val < 0) {
                                        setCantidad(0);
                                    } else {
                                        setCantidad(val);
                                    }
                                }}
                                fullWidth
                                inputProps={{ step: 1 }}
                            />

                        </Grid>
                        <Grid item xs={12} md={1}>
                            <FormControlLabel
                                size="small"
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={elegible}
                                        disabled={!poliza}
                                        onChange={(e) => {
                                            if (!poliza) return;
                                            setElegible(e.target.checked);
                                        }}
                                    />
                                }
                                label="Elegible"
                            />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <TextField size="small" label="Comentarios" value={comentarios || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={(e) => setComentarios(e.target.value)} />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button size="small" variant="contained" color="primary" onClick={handleAgregarAnalisis} disabled={!tienePermiso('Agregar Análisis a OT')}>Agregar</Button>
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Typography variant="h6">Análisis Agregados</Typography>
                                <IconButton size="small" onClick={() => setOpenInfo(true)}>
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Dialog open={openInfo} onClose={() => setOpenInfo(false)}>
                                <DialogTitle>Indicaciones</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        • Si el análisis es <strong>remitido</strong>, la fila se pintará de amarillo.<br />
                                        • Si es un análisis normal, no tendrá color alguno.
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setOpenInfo(false)} color="primary">
                                        Cerrar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <TextField
                                size="small"
                                label="Buscar análisis agregados"
                                variant="outlined"
                                fullWidth
                                value={searchAnalisisQuery}
                                onChange={e => {
                                    setSearchAnalisisQuery(e.target.value);
                                    // opcional: resetear página si cambias página
                                    setTablePage(0);
                                }}
                            />
                        </Grid>

                        {/* Tabla de análisis agregados */}
                        <Grid item xs={12}>

                            <TableContainer component={Paper}>
                                <Table size="small" sx={{
                                    // Opcional: fuerza menor padding vertical en todas las celdas
                                    '& .MuiTableCell-root': {
                                        py: 0.5,
                                        px: 1
                                    }
                                }}>
                                    <TableHead>
                                        <TableRow sx={{ height: 30 /* fila más corta */}}>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width: '10rem' }}>Acciones</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'150px' }}>Modulo</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width: '30rem'}}>Análisis</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width: '100px'  }}>Cantidad</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'8rem' }}>Precio</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'9rem' }}>Descuento</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'9rem'}}>Total</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'100px' }}>Elegible</TableCell>
                                            <TableCell sx={{ py: 0.5, px: 1, fontSize: '0.75rem', width:'20rem' }}>Comentarios</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analisisFiltrados
                                            .slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage)
                                            .map((analisis, index) => {
                                                const actualIndex = tablePage * rowsPerPage + index;
                                                return (
                                                    <TableRow 
                                                        key={actualIndex} 
                                                        onClick={() => {
                                                            // Simular clic en la casilla de verificación de elegible
                                                            handleElegibleChange(actualIndex);
                                                        }}
                                                        sx={{
                                                            backgroundColor: analisis.remitido ? '#FFF9C4' : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: analisis.remitido
                                                                    ? '#FFF59D'  // un poco más intenso al hacer hover
                                                                    : '#f5f5f5', // color gris claro para hover en filas normales
                                                            cursor: 'pointer', // Cambiar el cursor al pasar sobre la fila
                                                        }
                                                    }}>
                                                        <TableCell>
                                                            <Tooltip title={analisis.facturado ? "Item facturado - no puede eliminarse" : "Eliminar análisis"}>
                                                                <IconButton disabled={analisis.facturado || !tienePermiso('Eliminar Análisis de OT')} onClick={() => handleEliminarAnalisis(actualIndex)}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Ver condiciones">
                                                                <IconButton onClick={() => handleVerCondiciones(analisis)}>
                                                                    <VisibilityIcon color="primary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={analisis.facturado ? "Item facturado - no editable" : "Editar comentarios"}>
                                                                <IconButton disabled={analisis.facturado} onClick={() => handleAbrirModalEditarComentarios(analisis, actualIndex)}>
                                                                    <EditIcon color="secondary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}>{analisis.idModulo}</TableCell>
                                                        <TableCell>{analisis.analisis}</TableCell>
                                                        <TableCell>{analisis.cantidad}</TableCell>
                                                        <TableCell>{analisis.precio}</TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <span>{parseFloat(analisis.descuento).toFixed(2)}</span>
                                                                {(() => {
                                                                    const porcentaje = analisis.precio > 0 
                                                                        ? ((parseFloat(analisis.descuento) / parseFloat(analisis.precio)) * 100).toFixed(0) 
                                                                        : 0;
                                                                    return porcentaje > 0 ? (
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                minWidth: '32px',
                                                                                height: '32px',
                                                                                borderRadius: '50%',
                                                                                backgroundColor: '#4caf50',
                                                                                color: 'white',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 'bold',
                                                                                padding: '4px'
                                                                            }}
                                                                        >
                                                                            {porcentaje}%
                                                                        </Box>
                                                                    ) : null;
                                                                })()}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{parseFloat(analisis.total).toFixed(2)}</TableCell>
                                                        <TableCell>
                                                            <Checkbox
                                                                size="small"
                                                                checked={analisis.elegible}
                                                                disabled={!poliza}              // ← deshabilitado si no hay póliza
                                                                onChange={() => {
                                                                    if (!poliza) return;           // ← protección adicional
                                                                    handleElegibleChange(actualIndex);
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ maxWidth: 150, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                                            <Box component="div" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                                                {analisis.comentarios}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell align="right">
                                                <strong>Cantidad Analisis:</strong>
                                            </TableCell>
                                            <TableCell><strong>{cantidadTotal}</strong></TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            <TablePagination
                                rowsPerPageOptions={[50, 25, 10, 5]}
                                component="div"
                                count={analisisAgregados.length}
                                rowsPerPage={rowsPerPage}
                                page={tablePage}
                                onPageChange={handleChangeTablePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Filas por página:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                            />
                            </TableContainer>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextField label="Observaciones" value={observaciones || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={(e) => setObservaciones(e.target.value)} />
                        </Grid>
                        <Grid container item xs={12} justifyContent="flex-end" spacing={2} sx={{ mt: 0.4 }}>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    size="small"
                                    label="Valor Venta"
                                    type="number"
                                    value={valorVenta}
                                    onChange={(e) => setValorVenta(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    size="small"
                                    label="% Descuento"
                                    type="number"
                                    value={descuentoTotalP}
                                    onChange={(e) => setDescuentoTotalP(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }} // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    size="small"
                                    label="Descuento"
                                    type="number"
                                    value={descuentoTotal}
                                    onChange={(e) => setDescuentoTotal(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }} // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    size="small"
                                    label="Total a Pagar"
                                    type="number"
                                    value={totalPagar}
                                    onChange={(e) => setTotalPagar(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }}
                                // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} justifyContent="flex-start" spacing={2} sx={{ mt: 0.4 }}>
                            {/* Siempre mostramos el campo de Póliza sin importar si hay una póliza o no */}
                            <Grid item xs={12} md={3}>

                                {/* Los campos relacionados con la póliza se muestran solo si hay una póliza */}
                                {poliza && (
                                    <>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Poliza"
                                                value={poliza}
                                                onChange={(e) => setPoliza(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Empresa"
                                                type="text"
                                                value={empresa}
                                                onChange={(e) => setEmpresa(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="N° transaccion"
                                                value={nTransaccion}
                                                onChange={(e) => setNTransaccion(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: !(poliza && autorizado) }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Los bloques siguientes solo se muestran si hay una póliza */}
                            {poliza && (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Minimo"
                                                value={minimo}
                                                onChange={(e) => setMinimo(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Maximo"
                                                type="text"
                                                value={maximo}
                                                onChange={(e) => setMaximo(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="N° verificacion"
                                                value={nVerificacion}
                                                onChange={(e) => setNVerificacion(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: !(poliza && autorizado) }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="% Copago"
                                                value={copago}
                                                onChange={(e) => setCopago(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <FormControlLabel
                                                size="small"
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={autorizado}
                                                        onChange={(e) => setAutorizado(e.target.checked)}
                                                    />
                                                }
                                                label="Autorizado"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Deducible"
                                                value={deducible}
                                                onChange={(e) => setDeducible(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: !(poliza && autorizado) }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Descuento Asegurado"
                                                value={descuentoAsegurado}
                                                onChange={(e) => setDescuentoAsegurado(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: !(poliza && autorizado) }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Elegible"
                                                value={valorElegible}
                                                onChange={(e) => setValorElegible(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="No Elegible"
                                                type="text"
                                                value={noElegibles}
                                                onChange={(e) => setNoElegibles(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Aseguradora"
                                                value={aseguradora}
                                                onChange={(e) => setAseguradora(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Copago"
                                                value={valorCopago}
                                                onChange={(e) => setValorCopago(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                size="small"
                                                label="Total Asegurado"
                                                value={totalAsegurado}
                                                onChange={(e) => setTotalAsegurado(e.target.value)}
                                                fullWidth
                                                inputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Button size="small" variant="contained" color="primary" disabled={isSaving || !tienePermiso('Crear Orden de Trabajo')} onClick={handleGuardarOrdenTrabajo}>Guardar Orden de Trabajo</Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenComentariosEntregaModal(true)}
                                style={{ marginLeft: '10px' }}
                                disabled={!tienePermiso('Comentarios Entrega OT')}
                            >
                                Comentarios de Entrega
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={onClose}
                                style={{ marginLeft: '10px' }}
                            >
                                Salir
                            </Button>
                            {isModificable && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={onClose}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Cerrar
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <Modal
                        open={openModalCliente}
                        onClose={handleCloseModalCliente}
                        aria-labelledby="modal-title"
                        aria-describedby="modal-description"
                    >
                        <div style={modalStyle}>
                            <GestionClientes
                                cliente={clienteSeleccionado}
                                onClienteGuardado={handleClienteActualizado}
                                onClose={handleCloseModalCliente}
                            />
                        </div>
                    </Modal>

                    <ResultadoModal
                        open={showModal}
                        onClose={() => setShowModal(false)}
                        resultado={apiResult}
                    />
                    {showGestionFactura && (
                        <Modal
                            open={showGestionFactura}
                            onClose={() => setShowGestionFactura(false)}
                            aria-labelledby="modal-title-factura"
                            aria-describedby="modal-description-factura"
                        >
                            <div style={modalStyle}>
                                
                                
                                <GestionFactura
                               
                                    datosFactura={datosFactura}

                                    onClose={() => setShowGestionFactura(false)}
                                />
                              
                            
                            </div>
                        </Modal>
                     )}
                    {openReimprimirPreview && (
                        <Modal
                            open={openReimprimirPreview}
                            onClose={handleCloseReimprimirPreview}
                            aria-labelledby="modal-title-reimprimir"
                            aria-describedby="modal-description-reimprimir"
                        >
                            <div style={modalStyle}>
                                <h2>Vista Previa de Reimpresión</h2>
                                {reimprimirLoading && (
                                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="400px">
                                        <CircularProgress size={60} />
                                        <Typography variant="body1" style={{ marginTop: 16 }}>
                                            Generando vista previa del PDF...
                                        </Typography>
                                    </Box>
                                )}
                                {reimprimirError && (
                                    <Box p={3}>
                                        <Alert severity="error">{reimprimirError}</Alert>
                                    </Box>
                                )}                               
                                 {reimprimirPdfUrl && !reimprimirLoading && !reimprimirError && (
                                     <Box style={{ width: '100%', height: '100%' }}>
                                    <iframe
                                        src={reimprimirPdfUrl}
                                        style={{ 
                                            width: '100%', 
                                            height: '80vh', 
                                            border: 'none', 
                                            display: 'block',
                                            maxWidth: '100%'
                                        }}
                                        title="Vista previa del PDF"
                                    />
                                        <Box mt={2} display="flex" justifyContent="center" gap={2}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => {
                                                    handleCloseReimprimirPreview();
                                                    // Si estamos en modo modificar, cerrar el modal principal también
                                                    if (modo === 'modificar' && typeof onClose === 'function') {
                                                        onClose();
                                                    }
                                                }}
                                                size="large"
                                            >
                                                {modo === 'modificar' ? 'Cerrar' : 'Cancelar'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="info"
                                                onClick={handleImprimirEtiquetas}
                                                size="large"
                                            >
                                                Imprimir Etiquetas
                                            </Button>                                                    
                                                    <Button
    variant="contained"
    color="primary"
    onClick={async () => {
        // 1️⃣ Validar caja abierta
        if (!cajaAbierta) {
            swalWithHighZIndex.fire({
                title: 'Error',
                text: 'No hay caja abierta. Por favor, inicie la caja antes de facturar.',
                icon: 'error',
                zIndex: 2000
            });
            return;
        }

        try {
            // 2️⃣ Obtener id de la orden
            const idOrden = previewOrdenId || (ordenTrabajo && ordenTrabajo.idOrdenMedica);

            // 3️⃣ Llamar endpoint para obtener datos completos para facturación
            const response = await fetch(`${server}/MgOrdenesMedicas/ordenParaFacturar/${idOrden}`);
            if (!response.ok) {
                throw new Error('Error al obtener datos de la orden para facturación');
            }
            const datosOrden = await response.json();

            console.log('Datos que se enviarán a facturación:', datosOrden);

            // 4️⃣ Preparar objeto para GestionFactura
            const facturaDataToUse = {
                multiple: datosOrden.multiple ?? false,
                idOrdenMedica: datosOrden.idOrdenMedica,
                ordenTrabajo: datosOrden.ordenTrabajo,
                nombreCliente: datosOrden.nombreCliente ?? '',
                identidad: datosOrden.identidad ?? '',
                idCliente: datosOrden.idCliente ?? '',
                idSucursal: datosOrden.idSucursal ?? '',
                
                valorDescuento: datosOrden.valorDescuento ?? 0,
                valorVenta: datosOrden.valorVenta,
                valorPagar: datosOrden.valorVenta - datosOrden.valorDescuento,
                // observaciones: datosOrden.observaciones ?? '',
                // poliza: datosOrden.poliza ?? null,
                // aseguradora: datosOrden.aseguradora ?? null,
                // copago: datosOrden.copago ?? 0,
                // elegible: datosOrden.elegible ?? 0,
                // noElegible: datosOrden.noElegible ?? 0
            };

            // 5️⃣ Abrir modal según modo
            if (modo === 'modificar' && typeof onClose === 'function') {
                onClose({
                    shouldOpenFactura: true,
                    ordenData: facturaDataToUse
                });
            } else {
                setDatosFactura(facturaDataToUse);
                setShowGestionFactura(true);
            }
        } catch (error) {
            console.error('Error al preparar datos para facturación:', error);
            swalWithHighZIndex.fire({
                title: 'Error',
                text: `No se pudo preparar la factura: ${error.message}`,
                icon: 'error',
                zIndex: 2000
            });
        }
    }}
    size="large"
>
    Facturar
</Button>
                             </Box>
                                    </Box>
                                )}
                            </div>
                        </Modal>
                    )}
                    {openModalCondiciones && selectedAnalisis && (
                        <CondicionesUtilidadesModal
                            open={openModalCondiciones}
                            onClose={() => setOpenModalCondiciones(false)}
                            analysisId={selectedAnalisis}
                        />
                    )}
                    {openComentariosEntregaModal && (
                        <ComentariosEntregaModal
                            open={openComentariosEntregaModal}
                            onClose={() => setOpenComentariosEntregaModal(false)}
                            onSave={handleGuardarComentariosEntrega}
                            initialComentarios={comentariosEntrega}
                        />
                    )}
                    <Dialog
                        open={openConfirmationDialog}
                        onClose={handleCloseConfirmationDialog}
                    >
                        <DialogTitle>Despachar Factura</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                ¿Desea despachar la factura?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseConfirmationDialog} color="primary">
                                No
                            </Button>
                            <Button onClick={handleConfirmDespachar} color="primary" autoFocus>
                                Sí
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Modal para editar comentarios de análisis */}
                    <Dialog 
                        open={openModalEditarComentarios} 
                        onClose={handleCerrarModalEditarComentarios}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            Editar Detalle - {analisisParaEditar?.analisis}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 2 }}>
                                Modifica los detalles para este análisis:
                            </DialogContentText>
                            
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                {/* Tipo de Precio */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Tipo Precio</InputLabel>
                                        <Select
                                            value={tipoPrecioEditando}
                                            onChange={(e) => {
                                                const nuevoTipoPrecio = e.target.value;
                                                setTipoPrecioEditando(nuevoTipoPrecio);
                                                // Actualizar precio según el tipo seleccionado
                                                const precioSeleccionado = tiposPrecioAnalisisEdicion.find(p => p.idTipoPrecio === parseInt(nuevoTipoPrecio));
                                                if (precioSeleccionado) {
                                                    setPrecioEditando(precioSeleccionado.precio);
                                                }
                                            }}
                                            label="Tipo Precio"
                                            disabled={analisisParaEditar?.facturado}
                                        >
                                            {tiposPrecioAnalisisEdicion.map((tp) => (
                                                <MenuItem key={tp.idTipoPrecio} value={tp.idTipoPrecio}>
                                                    {tp.descripcionTipoPrecio}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Elegible */}
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={elegibleEditando}
                                                onChange={(e) => setElegibleEditando(e.target.checked)}
                                                disabled={!poliza || analisisParaEditar?.facturado}
                                            />
                                        }
                                        label="Elegible para Póliza"
                                    />
                                </Grid>

                                {/* Precio (solo lectura, se ajusta al cambiar tipo de precio) */}
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Precio"
                                        value={precioEditando}
                                        disabled
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Descuento (%)"
                                    type="number"
                                    value={descuentoPorcentajeEditando}
                                    inputProps={{ step: "0.01", readOnly: !puedeCambiarDescuento }}
                                    onChange={(e) => {
                                        const porcentaje = Math.min(Math.max(parseFloat(e.target.value) || 0, 0), 100);
                                        setDescuentoPorcentajeEditando(porcentaje);
                                        setDescuentoEditando(precioEditando * (porcentaje / 100));

                                        //Marcar como manual para que no se sobreescriba
                                        // descuentoManualRef.current = true;

                                        // Tambien marcar el estado del analisis para guardarlo
                                        setAnalisisParaEditar(prev => ({ ...prev, descuentoManual: true, porcentajeManual: porcentaje}));
                                    }}
                                />
                                </Grid>

                                {/* Descuento (solo lectura, refleja cálculo actual) */}
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Descuento"
                                        value={descuentoEditando}
                                        disabled
                                        variant="outlined"
                                        InputProps={{ readOnly: true }}
                                        helperText={precioEditando > 0 ? `${((descuentoEditando / precioEditando) * 100).toFixed(0)}%` : ''}
                                    />
                                </Grid>

                                {/* Total (calculado) */}
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Total"
                                        value={(precioEditando - descuentoEditando).toFixed(2)}
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Grid>

                                {/* Comentarios */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        label="Comentarios"
                                        value={comentarioEditando}
                                        onChange={(e) => setComentarioEditando(e.target.value)}
                                        placeholder="Escribe los comentarios para este análisis..."
                                        disabled={analisisParaEditar?.facturado}
                                    />
                                </Grid>
                            </Grid>

                            {analisisParaEditar?.facturado && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Este análisis está facturado y no puede ser modificado.
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCerrarModalEditarComentarios} color="secondary">
                                Cancelar
                            </Button>
                            <Button 
                                onClick={handleGuardarComentario} 
                                color="primary" 
                                variant="contained"
                                disabled={analisisParaEditar?.facturado}
                            >
                                Guardar
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Modal para seleccionar etiquetas a imprimir */}
                    <Dialog
                        open={openLabelModal}
                        onClose={handleCloseLabelModal}
                        maxWidth="lg"
                        fullWidth
                    >
                        <DialogTitle>
                            Seleccionar Muestras para Etiquetas
                        </DialogTitle>
                        <DialogContent>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            availableSamples.length > 0 && 
                                            availableSamples.every(m => selectedSamples.includes(m.idMuestraXAnalisis))
                                        }
                                        onChange={(e) => handleSelectAllSamples(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Seleccionar todas las muestras"
                            />
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Seleccionar</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: '100px' }}>Módulo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: '150px' }}>Descripción</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Cantidad</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: '200px' }}>Examenes</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Cobas 303</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {availableSamples.map((muestra, index) => (
                                            <TableRow key={`${muestra.idMuestraXAnalisis}-${index}`}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedSamples.includes(muestra.idMuestraXAnalisis)}
                                                        onChange={(e) => handleSampleSelectionChange(muestra, e.target.checked)}
                                                    />
                                                </TableCell>
                                                    <TableCell>{muestra.modulo || 'N/A'}</TableCell>
                                                    <TableCell>{muestra.descripcionMuestra}</TableCell>
                                                    <TableCell>{muestra.descripcionCantidad}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ fontSize: '0.85rem' }}>
                                                            {muestra.ComparteCon}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={muestra.esCobas303 || false}
                                                            onChange={(e) => {
                                                                const updatedSamples = [...availableSamples];
                                                                updatedSamples[index] = {
                                                                    ...muestra,
                                                                    esCobas303: e.target.checked
                                                                };
                                                                setAvailableSamples(updatedSamples);
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseLabelModal} color="secondary" variant="outlined">
                                Cancelar
                            </Button>
                            <Button
                                onClick={handlePrintSelectedLabels}
                                color="primary"
                                variant="contained"
                                disabled={selectedSamples.length === 0}
                            >
                                Imprimir Etiquetas
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </div>
        </>
    );
}

export default GestionOrdenTrabajo;
